/**
 * SASS AI-Rooster Engine — ES Module versie voor de browser
 * Origineel: src/rooster-engine.js (CommonJS, niet aanraken)
 *
 * Sessie 4: pre-commit vaste bezette uren + sortering gemeente-volgorde
 *   - Kantoordagen tellen als bezette uren (niet inzetbaar op straat)
 *   - School op werktijd (week B) telt als bezette uren
 *   - Gemeenten met vaste medewerkers worden eerst verwerkt (Rheden vóór Arnhem)
 *     zodat die uren geclaimd zijn voordat algemene gemeenten datzelfde budget verbruiken
 */

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

// Terugvalwaarde voor instellingen.max_aaneengesloten_werkdagen (regels.json). De regelset
// van een LIVE omgeving komt uit de database, niet uit regels.json; zonder deze terugval
// zou de max-N-regel daar pas gelden na een handmatige datamigratie. Bijstellen hoort via
// de instelling te gebeuren — dit getal is het vangnet, niet de knop.
export const MAX_AANEENGESLOTEN_WERKDAGEN = 5;

// Teamleider-only (rollen zonder 'medewerker'): nooit ingeroosterd én uit de uren-samenvatting.
// Afwezig rollen-veld geldt als ['medewerker'].
export function isTeamleiderOnly(mw) { return Array.isArray(mw?.rollen) && !mw.rollen.includes('medewerker'); }

export function isBeschikbaar(mwKey, mw, dag, gemeenteKey, afwezigheid, swType, weeknummer = null, vakantieDagen = null) {
  if (isTeamleiderOnly(mw)) return false;
  const afw = afwezigheid[mw.naam];
  if (afw && (afw instanceof Set ? afw.has(dag) : afw.includes(dag))) return false;
  if (mw.vaste_vrije_dagen?.includes(dag)) return false;
  if (mw.kantoor_dagen?.includes(dag)) return false;
  if (mw.school?.dag === dag && !schoolVervalt(mw, vakantieDagen)) return false;
  if (mw.werkdagen?.length && !mw.werkdagen.includes(dag)) return false;
  if (!mw.gemeenten?.includes(gemeenteKey)) return false;

  // Weekend-rotatie: groep A = ON in oneven weken, OFF in even weken.
  //                   groep B = ON in even weken, OFF in oneven weken.
  // Splitst het team zodat de helft elk weekend beschikbaar is.
  // Blokkeert hier alleen za/zo in de OFF-week; de vr/za/zo-exclusiviteit voor
  // de eigen weekend-gemeente staat hieronder (weeknummer-onafhankelijk).
  if (mw.weekend_rotatie === true && weeknummer !== null) {
    const groep = mw.weekend_groep || 'A';
    const isOff = groep === 'A' ? weeknummer % 2 === 0 : weeknummer % 2 !== 0;
    if (isOff) {
      if (dag === 'za' || dag === 'zo') return false;
    }
  }

  // Weekend-exclusiviteit: wie een vaste weekend-gemeente heeft, staat op vr/za/zo
  // NOOIT in een andere gemeente - elke week (ON en OFF) - en lekt dus niet weg
  // naar bv. Arnhem-dagdienst of de weekend-noodvulling. Bewust weeknummer-
  // onafhankelijk: ook relaxWeekend (weeknummer null) kan hier niet omheen.
  // Medewerkers zonder vast_weekend_gemeente (afwisselend) blijven ongemoeid.
  if ((dag === 'vr' || dag === 'za' || dag === 'zo') && mw.vast_weekend_gemeente && gemeenteKey !== mw.vast_weekend_gemeente) return false;

  return true;
}

// Bezettingsdoel: ALTIJD in PERSONEN, voor elke dienst hetzelfde — geen tweedeling meer op
// oneven_toegestaan. Een dienst mag zijn doel rechtstreeks in personen zetten (min_personen /
// max_personen); staat dat er niet, dan is het koppels x grootte. De ONDERGRENS is het minimum
// dat de pas moet halen, de BOVENGRENS (max=true) waartoe hij mag VULLEN-TOT-BESCHIKBAAR.
// oneven_toegestaan stuurt hier niets meer aan; die vlag regelt alleen nog de koppel-pariteit
// elders (donor-swap-gate, herschik-pariteitsguard, LET OP-log).
// Geexporteerd zodat UI/verlof-check exact hetzelfde minimum gebruiken.
export function berekenBezettingsDoel(d, max, koppelGrootte = 2) {
  const kg = koppelGrootte || 2;
  return max
    ? (d.max_personen ?? (d.max_koppels || d.min_koppels || 1) * kg)
    : (d.min_personen ?? (d.min_koppels || 1) * kg);
}

export function schoolweekType(weeknummer) {
  return weeknummer % 2 === 1 ? 'A' : 'B';
}

/**
 * Vervalt de schooldag van deze medewerker deze week? In een schoolvakantie is er geen
 * les: de dag komt vrij voor straatdienst en de schooluren tellen niet als vaste bezetting.
 * vakantieDagen = Set<dag-code> van DEZE week; null/leeg → niets vervalt (oud gedrag).
 */
export function schoolVervalt(mw, vakantieDagen = null) {
  return !!(mw?.school?.dag && vakantieDagen?.has(mw.school.dag));
}

/**
 * Telt de schooldag van deze medewerker als werktijd in dit weektype?
 * Regeling 'afwisselend_werktijd_eigentijd' = om-en-om: alleen in week A is school
 * betaalde werktijd (telt mee als uren, S-blok zichtbaar). In week B is het eigen tijd
 * → telt NIET als gewerkte uren en de maandag blijft leeg.
 * Andere/ontbrekende regeling → school telt elke week (volledig werktijd).
 */
export function schoolTeltAlsWerk(mw, weekType, vakantieDagen = null) {
  if (!mw.school) return false;
  if (schoolVervalt(mw, vakantieDagen)) return false;
  if (mw.school.regeling === 'afwisselend_werktijd_eigentijd') return weekType === 'A';
  return true;
}

/**
 * Telt hoeveel dagen een medewerker deze week überhaupt op straat inzetbaar is
 * (bovengrens, los van gemeente/slot-capaciteit). Gebruikt door de budget-verdeling
 * om medewerkers met krappe latere weken (bv. Danny: ma school + wo vrij + weekend-rotatie)
 * vooraan te laten bijladen i.p.v. een gelijkmatige verdeling die hen tekort doet.
 */
function beschikbareStraatDagen(mw, weeknummer, afwezigDagen = null, vakantieDagen = null) {
  const afwezig = (dag) => afwezigDagen &&
    (afwezigDagen instanceof Set ? afwezigDagen.has(dag) : afwezigDagen.includes(dag));
  let n = 0;
  for (const dag of DAGEN) {
    if (afwezig(dag)) continue;
    if (mw.vaste_vrije_dagen?.includes(dag)) continue;
    if (mw.kantoor_dagen?.includes(dag)) continue;
    if (mw.school?.dag === dag && !schoolVervalt(mw, vakantieDagen)) continue; // schooldag: nooit op straat (beide weken), tenzij vakantie
    if (mw.werkdagen?.length && !mw.werkdagen.includes(dag)) continue;
    if (mw.weekend_rotatie === true) {
      const groep = mw.weekend_groep || 'A';
      const isOff = groep === 'A' ? weeknummer % 2 === 0 : weeknummer % 2 !== 0;
      if (isOff && (dag === 'za' || dag === 'zo')) continue;
    }
    n++;
  }
  return n;
}

// Heeft deze vast_rotatiepatroon-medewerker vandaag een dienst op zijn voorkeur-gemeente?
// Spiegelt EXACT de dienstdagen-afleiding die de kandidaat-filter gebruikt bij het
// inroosteren (het vast_rotatiepatroon-filter op heeftDienstVandaag in vulSlot), zodat
// "waar wordt hij ingepland" en "wat telt als werkdag" niet uit elkaar kunnen lopen.
function patroonDienstOpDag(mw, dag, weekType, regels) {
  if (!mw.vast_rotatiepatroon || !mw.voorkeur_gemeente || !regels) return false;
  const gem = regels.gemeenten?.[mw.voorkeur_gemeente];
  if (!gem || gem.actief === false) return false;
  return Object.values(gem.diensten || {}).some((d) => {
    const dagLijst = (weekType === 'A' && d.week_a_dagen) ? d.week_a_dagen
      : (weekType === 'B' && d.week_b_dagen) ? d.week_b_dagen
      : (d.week_a_dagen || d.week_b_dagen) ? []
      : (d.dagen || []);
    return dagLijst.includes(dag);
  });
}

// Zou deze persoon op deze dag normaal werken? (cap voor de afwezigheids-credit)
// = niet structureel vrij. Weekend telt alleen bij een duidelijke verplichting:
// rotatie-ON-week, vast rotatiepatroon-dienst, vaste weekend-gemeente of expliciete
// werkdagen-lijst.
// `regels` is optioneel (default null = gedrag van vóór de patroon-tak): zonder regels
// kan de patroon-check niet draaien en valt de functie terug op de andere signalen.
export function wouldBeWerkdag(mw, dag, weekType, weeknummer, regels = null, vakantieDagen = null) {
  if (mw.vaste_vrije_dagen?.includes(dag)) return false;
  if (mw.werkdagen?.length && !mw.werkdagen.includes(dag)) return false;
  // Vervalt de schooldag (schoolvakantie), dan valt deze regel door en is het een gewone
  // werkdag. schoolTeltAlsWerk krijgt hier BEWUST geen vakantieDagen: dat zou de check
  // dubbel maken (schoolVervalt staat al links van de &&).
  if (mw.school?.dag === dag && !schoolVervalt(mw, vakantieDagen) && !schoolTeltAlsWerk(mw, weekType)) return false;
  if (dag === 'za' || dag === 'zo') {
    if (mw.weekend_rotatie === true) {
      const groep = mw.weekend_groep || 'A';
      const isOff = groep === 'A' ? weeknummer % 2 === 0 : weeknummer % 2 !== 0;
      return !isOff;
    }
    // Vast rotatiepatroon (Haruun/Myrthe = Lingewaard-specialisten): in hun patroon-week
    // draait de engine ze écht op het weekend van hun voorkeur-gemeente — de comment bij
    // de Arnhem-weekend-ban in vulSlot zegt het met zoveel woorden. Zonder deze tak gaf
    // wouldBeWerkdag daar false: geen V/Z in de matrix (roostercodes.getCel) en geen
    // verlof-credit (de wbDagen-filter in draaiMaandrooster), terwijl ze wel ingepland
    // stonden. Ze hebben géén weekend_rotatie/vast_weekend_gemeente, dus geen van de
    // andere signalen ving dit op.
    if (patroonDienstOpDag(mw, dag, weekType, regels)) return true;
    return !!mw.vast_weekend_gemeente || !!mw.werkdagen?.length;
  }
  return true;
}

/**
 * Is dit voor deze medewerker een GEWERKTE dag? Exact de definitie waarmee de reeksen zijn
 * doorgemeten (client/test/diag/reeks-aaneengesloten-dagen.mjs, functie watWas):
 *
 *   - afwezig op die dag (verlof/ziekte/anders) -> false, en dat BREEKT de reeks, ook als de
 *     persoon nog gewoon in het rooster staat
 *   - staat in rooster[gemeente][dienst][dag]   -> true (elke gemeente, elke dienst)
 *   - kantoor_dagen bevat de dag                -> true
 *   - schooldag én schoolTeltAlsWerk(...)       -> true
 *   - anders                                    -> false
 *
 * ⚠ NIET wouldBeWerkdag. Die beantwoordt een andere vraag — "zou deze persoon hier
 * STRUCTUREEL werken" — en geeft true voor vrijwel elke gewone weekdag, ongeacht wat er in
 * het rooster staat; hij is de cap voor de afwezigheids-credit. Deze functie kijkt naar wat
 * er FEITELIJK op die dag staat.
 *
 * `week`  = een engine-weekobject (rooster + schoolweekType + weeknummer); ontbreekt
 *           schoolweekType (oud opgeslagen weekobject), dan valt het terug op het weeknummer.
 * `afwezigheidMap` = de map van bouwAfwezigheidMap voor DIE week — gesleuteld op NAAM, niet
 *           op medewerker-key. Set of array, beide vormen worden gelezen.
 * `vakantieDagen`  = Set<dag-code> van DIE week (schoolvakantie), of null.
 *
 * Puur lezend: muteert niets.
 */
export function isGewerkteDag(mw, dag, week, afwezigheidMap = null, vakantieDagen = null) {
  if (!mw || !week) return false;

  const afw = afwezigheidMap?.[mw.naam];
  if (afw && (afw instanceof Set ? afw.has(dag) : afw.includes(dag))) return false;

  const rooster = week.rooster || {};
  for (const gemKey of Object.keys(rooster)) {
    for (const dienstId of Object.keys(rooster[gemKey])) {
      const namen = rooster[gemKey][dienstId]?.[dag];
      if (Array.isArray(namen) && namen.includes(mw.naam)) return true;
    }
  }

  if (mw.kantoor_dagen?.includes(dag)) return true;
  const weekType = week.schoolweekType ?? schoolweekType(week.weeknummer);
  if (mw.school?.dag === dag && schoolTeltAlsWerk(mw, weekType, vakantieDagen)) return true;
  return false;
}

/**
 * Hoeveel aaneengesloten GEWERKTE dagen liggen er direct VÓÓR (weeknummer, dag)?
 * Telt terug vanaf de dag ervóór en stopt bij de eerste niet-gewerkte dag. De opgegeven dag
 * zelf telt NIET mee — de vraag is "hoe lang loopt de reeks al" op het moment dat je over
 * die dag beslist. Puur lezend: muteert niets.
 *
 * `weken` is een CHRONOLOGISCH GEORDENDE, AANEENGESLOTEN reeks weekobjecten waarin de
 * gevraagde week voorkomt. Aaneengesloten is een eis aan de aanroeper, geen numerieke
 * controle hier: over de jaargrens loopt week 52/53 gewoon door naar week 1, dus een
 * weeknummer+1-check zou daar juist fout gaan. Zit de gevraagde week er niet in -> 0.
 *
 * Is het begin van `weken` bereikt zonder een niet-gewerkte dag, dan is de uitkomst een
 * ONDERGRENS: verder terugkijken kan niet. Voor een max-7-dagen-regel is één week
 * voorgeschiedenis genoeg, want de terugblik reikt nooit verder dan 7 dagen.
 *
 * ctx: { afwMaps, vakantieMaps } — beide op WEEKNUMMER gesleuteld, precies zoals
 * draaiMaandrooster ze al heeft. Ontbreekt een week daarin, dan telt die week zonder
 * afwezigheid/vakantiedagen.
 */
export function telAaneengeslotenGewerkteDagen(weken, mw, weeknummer, dag, { afwMaps = null, vakantieMaps = null } = {}) {
  if (!Array.isArray(weken) || !mw) return 0;
  const wIdx = weken.findIndex(w => w?.weeknummer === weeknummer);
  const dIdx = DAGEN.indexOf(dag);
  if (wIdx === -1 || dIdx === -1) return 0;

  let n = 0;
  for (let wi = wIdx, di = dIdx - 1; wi >= 0; wi--, di = DAGEN.length - 1) {
    const week = weken[wi];
    const afwMap = afwMaps?.[week?.weeknummer] || null;
    const vakDagen = vakantieMaps?.[week?.weeknummer] || null;
    for (; di >= 0; di--) {
      if (!isGewerkteDag(mw, DAGEN[di], week, afwMap, vakDagen)) return n;
      n++;
    }
  }
  return n;
}

/**
 * Spiegelbeeld van telAaneengeslotenGewerkteDagen: hoeveel aaneengesloten GEWERKTE dagen
 * liggen er direct NÁ (weeknummer, dag)? Telt vooruit vanaf de dag erna en stopt bij de
 * eerste niet-gewerkte dag; de opgegeven dag zelf telt NIET mee.
 *
 * Waarom dit nodig is naast de terugtelling: de engine vult NIET chronologisch — eerst de
 * weekenden, dan de weekdag-dagdiensten, dan de late diensten, dan pas fase 2. Wie alleen
 * terugtelt ziet bij het vullen van woensdag een lege maandag en dinsdag, keurt goed, en
 * keurt daarna maandag en dinsdag óók goed omdat die elk apart nog binnen de grens vallen.
 * Samen leveren ze een te lange reeks. Met beide richtingen ziet elke plaatsing wat er op
 * dat moment aan BEIDE kanten staat, en breekt de laatste plaatsing van de keten af.
 *
 * Eisen aan `weken` en `ctx` zijn identiek aan de terugtelling. Is het EINDE van `weken`
 * bereikt zonder een niet-gewerkte dag, dan is de uitkomst een ONDERGRENS: verder
 * vooruitkijken kan niet. In genereerRooster is dat de normale situatie — de huidige week
 * is de laatste in de historie, want latere weken bestaan nog niet.
 */
export function telAaneengeslotenGewerkteDagenNa(weken, mw, weeknummer, dag, { afwMaps = null, vakantieMaps = null } = {}) {
  if (!Array.isArray(weken) || !mw) return 0;
  const wIdx = weken.findIndex(w => w?.weeknummer === weeknummer);
  const dIdx = DAGEN.indexOf(dag);
  if (wIdx === -1 || dIdx === -1) return 0;

  let n = 0;
  for (let wi = wIdx, di = dIdx + 1; wi < weken.length; wi++, di = 0) {
    const week = weken[wi];
    const afwMap = afwMaps?.[week?.weeknummer] || null;
    const vakDagen = vakantieMaps?.[week?.weeknummer] || null;
    for (; di < DAGEN.length; di++) {
      if (!isGewerkteDag(mw, DAGEN[di], week, afwMap, vakDagen)) return n;
      n++;
    }
  }
  return n;
}

/**
 * Controleert of iemand een late dienst heeft op een bepaalde dag (in enige gemeente).
 * Gebruikt voor de rustregeling: geen dagdienst de ochtend ná een late dienst.
 */
function heeftLateDienstOp(naam, dag, rooster) {
  return Object.values(rooster).some(gemRooster =>
    Object.entries(gemRooster).some(([dienstId, dagmap]) =>
      dienstId.startsWith('late_dienst') && Array.isArray(dagmap[dag]) && dagmap[dag].includes(naam)
    )
  );
}

/**
 * Controleert of iemand een dagdienst heeft op een bepaalde dag.
 * Tegenhanger van heeftLateDienstOp: nodig voor de bidirectionele rustregel als
 * dagdiensten vóór late diensten worden ingepland (dan moet de late-dienst-toewijzing
 * checken of de persoon de ochtend erna al een dagdienst heeft).
 */
function heeftDagdienstOp(naam, dag, rooster) {
  return Object.values(rooster).some(gemRooster =>
    Object.entries(gemRooster).some(([dienstId, dagmap]) =>
      dienstId === 'dagdienst' && Array.isArray(dagmap[dag]) && dagmap[dag].includes(naam)
    )
  );
}

/**
 * CAO-grens check op WEEK-niveau — bewust uitgeschakeld (geeft altijd []).
 * De week-cap is niet de juiste eenheid om CAO aan te toetsen; zie de toelichting hieronder.
 */
export function controleerCAOGrenzen() {
  // Week-niveau CAO-melding bewust uitgeschakeld: een week boven de week-cap is geen
  // overtreding zolang het blok-totaal klopt (iemand draait over het blok z'n contracturen).
  // De blok-balans wordt al door de suggesties gedekt; de echte uren-begrenzing tijdens
  // generatie zit in vulSlot (rooster-engine.js ~491-495) en staat hier los van.
  // Parameters verwijderd (waren ongebruikt); de twee call-sites geven nog args mee maar
  // JS negeert die — controleerCAOGrenzen(...) blijft daardoor ongewijzigd werken.
  return [];
}

/**
 * Berekent de uren die structureel bezet zijn voor een medewerker,
 * dus vóórdat de engine ook maar één dienst heeft ingepland:
 *
 *   - Kantoordagen: werkt die dag, maar niet op straat inzetbaar
 *   - School op werktijd: alleen in week A (afwisselende regeling) telt school als uren
 *
 * Vaste weekend-gemeente uren worden NIET hier pre-committed, maar via
 * procesvolgorde (sorteerGemeenten) zodat er geen dubbelcount optreedt.
 */
export function berekenVasteBezeteUren(mw, weekType, afwezigDagen = null, vakantieDagen = null) {
  const afwezig = (dag) => afwezigDagen &&
    (afwezigDagen instanceof Set ? afwezigDagen.has(dag) : afwezigDagen.includes(dag));
  let uren = 0;

  if (mw.kantoor_dagen?.length) {
    uren += mw.kantoor_dagen.filter(dag => !afwezig(dag)).length * 8;
  }

  // School telt alleen als bezette/gewerkte uren in de werktijd-week (week A bij
  // afwisselende regeling). In week B is school eigen tijd → 0 uren, maandag blijft leeg.
  // (De maandag-blokkering in isBeschikbaar geldt wél elke week: de persoon zit op school.)
  if (schoolTeltAlsWerk(mw, weekType, vakantieDagen) && !afwezig(mw.school.dag)) {
    uren += mw.school.uren ?? 8;
  }

  return uren;
}

/**
 * Sorteert gemeenten in drie prioriteitslagen zodat uren in de juiste volgorde
 * worden geclaimd voordat algemene gemeenten (Arnhem) het budget opgebruiken:
 *
 *   0 — vast weekend (vast_weekend_gemeente + weekend_rotatie===false): Rheden
 *   1 — voorkeur-gemeente (voorkeur_gemeente): Lingewaard  ← Haruun & Myrthe
 *   2 — overige gemeenten: Arnhem
 */
function sorteerGemeenten(gemeenten, medewerkers) {
  const prioriteit = (gemKey) => {
    const mwLijst = Object.entries(medewerkers).filter(([k]) => !k.startsWith('_'));
    if (mwLijst.some(([, mw]) => mw.vast_weekend_gemeente === gemKey && mw.weekend_rotatie === false))
      return 0;
    if (mwLijst.some(([, mw]) => mw.voorkeur_gemeente === gemKey))
      return 1;
    return 2;
  };

  return Object.entries(gemeenten)
    .filter(([k, g]) => !k.startsWith('_') && g.actief !== false)
    .sort(([ka], [kb]) => prioriteit(ka) - prioriteit(kb));
}

/**
 * weekCaps: optioneel object { [mwKey]: maxUrenDezeWeek } — de RUIME cap voor minimumbezetting
 *   (Fase 1). Bij 4-weken planning berekent genereerMaandrooster dit op basis van resterend
 *   budget en compensatieregels; anders geldt contract_uren.
 * weekCapsExtra: optionele KRAPPERE cap voor extra koppels (Fase 2). Throttelt extra's tot een
 *   gelijkmatig tempo zodat budget niet in vroege weken wordt opgesoupeerd — anders bleven
 *   minimum-slots in de laatste week onbemand. Valt terug op weekCaps als niet meegegeven.
 * voorafgaandeWeken: de weken die chronologisch VÓÓR deze week liggen, op volgorde: de al
 *   gegenereerde weken van dit blok, eventueel voorafgegaan door de VASTGELEGDE week van net
 *   vóór de blokgrens (zie draaiMaandrooster). Voedt telAaneengeslotenGewerkteDagen, en
 *   daarmee de max-aaneengesloten-werkdagen-regel. Default [] = starten zonder terugblik: de
 *   reeks begint dan bij maandag van DEZE week.
 * historieCtx: { afwMaps, vakantieMaps } — beide op WEEKNUMMER gesleuteld, zoals
 *   draaiMaandrooster ze al heeft. Nodig omdat de reeks-teller óók in voorafgaandeWeken moet
 *   weten wie er verlof had: zonder die maps telt een kantoor- of schooldag die in
 *   werkelijkheid een verlofdag was mee als gewerkt, en valt de regel dus te streng uit.
 *   De HUIDIGE week wordt hieronder altijd zelf toegevoegd (uit afwezigheid/vakantieDagen),
 *   dus die hoeft er niet in te zitten. Default null = alleen de huidige week is bekend.
 */
export function genereerRooster(regels, afwezigheid, weeknummer, weekCaps = null, weekCapsExtra = null, maandTekort = null, herschikWhitelist = null, vakantieDagen = null, voorafgaandeWeken = [], historieCtx = null) {
  const type = schoolweekType(weeknummer);
  const log = [];
  const herschikTrace = []; // [HERSCHIK-TRACE] (Fase 1): zachte observability voor de swap-laag; canonical/golden negeert dit
  const conflicten = [];
  const rooster = {};

  // ── Max aaneengesloten gewerkte dagen ──────────────────────────────────────
  // De grens komt uit de regelset zodat hij bijgesteld kan worden zonder de logica hier
  // aan te raken; MAX_AANEENGESLOTEN_WERKDAGEN is enkel de terugval voor regelsets die de
  // instelling nog niet hebben (zie de comment daar).
  const maxAaneengesloten = regels.instellingen?.max_aaneengesloten_werkdagen ?? MAX_AANEENGESLOTEN_WERKDAGEN;

  // De historie die de teller afloopt: de weken hiervóór, gevolgd door DEZE week. Het
  // rooster-object gaat bij REFERENTIE mee — het is nog leeg maar groeit mee terwijl we
  // vullen, dus een plaatsing op maandag telt vanzelf mee als we over dinsdag beslissen.
  const historieWeken = [...voorafgaandeWeken, { weeknummer, rooster, schoolweekType: type }];
  // De maps van deze week komen uit de eigen parameters; die van eerdere weken uit
  // historieCtx. Deze week wint bij een botsing — hier is de informatie het meest direct.
  const reeksCtx = {
    afwMaps: { ...(historieCtx?.afwMaps || {}), [weeknummer]: afwezigheid },
    vakantieMaps: { ...(historieCtx?.vakantieMaps || {}), [weeknummer]: vakantieDagen },
  };
  // Hoeveel aaneengesloten gewerkte dagen liggen er DIRECT VÓÓR resp. NÁ `dag`? De dag zelf
  // telt in beide niet mee. Samen met de dag zelf vormen ze de reeks die zou ontstaan:
  // reeksVoor + 1 + reeksNa. Beide richtingen zijn nodig omdat de engine niet chronologisch
  // vult — zie de comment bij telAaneengeslotenGewerkteDagenNa.
  const reeksVoor = (mw, dag) => telAaneengeslotenGewerkteDagen(historieWeken, mw, weeknummer, dag, reeksCtx);
  const reeksNa = (mw, dag) => telAaneengeslotenGewerkteDagenNa(historieWeken, mw, weeknummer, dag, reeksCtx);

  const latenTellerPerGem = {}; // { [mwKey]: { [gemKey]: count } } — per gemeente bijgehouden zodat Rheden-diensten Arnhem-prioriteit niet beïnvloeden
  const dagLaatTeller = {};     // { [mwKey]: { dag, laat } } — dag/late-diensten DEZE week, voor Flex-afwisseling
  const urenTeller = {};
  const medewerkerKeys = Object.keys(regels.medewerkers).filter(k => !k.startsWith('_'));

  // ── Stap 1: pre-commit vaste bezette uren ──────────────────────────────────
  medewerkerKeys.forEach(k => {
    latenTellerPerGem[k] = {};
    dagLaatTeller[k] = { dag: 0, laat: 0 };
    const mw = regels.medewerkers[k];
    const vooraf = berekenVasteBezeteUren(mw, type, null, vakantieDagen);
    urenTeller[k] = vooraf;

    if (vooraf > 0) {
      const beschikbaar = mw.contract_uren - vooraf;
      const maxDiensten = Math.floor(Math.max(0, beschikbaar) / 8);
      const redenen = [];
      if (mw.kantoor_dagen?.length) redenen.push(`${mw.kantoor_dagen.length}×kantoor`);
      if (mw.school && type === 'B') redenen.push('school op werktijd');
      log.push(
        `[BUDGET] ${mw.naam}: ${mw.contract_uren}u − ${vooraf}u (${redenen.join(', ')}) = ${beschikbaar}u vrij → max ${maxDiensten} diensten op straat`
      );
    }

    // Informeer over vaste weekend-gemeente (wordt via volgorde afgehandeld)
    if (mw.vast_weekend_gemeente && mw.weekend_rotatie === true) {
      const groep = mw.weekend_groep || 'A';
      const isOn = groep === 'A' ? weeknummer % 2 !== 0 : weeknummer % 2 === 0;
      log.push(`[BUDGET] ${mw.naam}: weekend-rotatie groep ${groep} → week ${weeknummer} is ${isOn ? `ON (werkt vr/za/zo ${mw.vast_weekend_gemeente})` : 'OFF (za/zo vrij, vr beschikbaar voor andere gemeente)'}`);
    }
    if (mw.vast_weekend_gemeente && mw.weekend_rotatie === false) {
      const gem = regels.gemeenten[mw.vast_weekend_gemeente];
      if (gem && gem.actief !== false) {
        // Cyclus-diensten (week_a_dagen/week_b_dagen) tellen als gemiddelde van
        // beide weekhelften — consistent met de bi-wekelijkse planning. Geen
        // cyclus → platte dagen-lijst (met vangnet tegen een ontbrekend veld).
        const dienstDagen = Object.values(gem.diensten).reduce((s, d) => {
          const aantal = (d.week_a_dagen || d.week_b_dagen)
            ? ((d.week_a_dagen?.length || 0) + (d.week_b_dagen?.length || 0)) / 2
            : (d.dagen?.length || 0);
          return s + aantal;
        }, 0);
        const gereserveerd = dienstDagen * 8;
        const resterend = mw.contract_uren - vooraf - gereserveerd;
        log.push(
          `[BUDGET] ${mw.naam}: +${gereserveerd}u voor ${gem.naam} (${dienstDagen} vaste diensten) → ${Math.max(0, resterend)}u over voor andere gemeenten`
        );
      }
    }
  });

  log.push(`[AI] Schoolweek-type ${type} voor week ${weeknummer}`);

  // ── Stap 2: gemeenten sorteren — vaste-weekend-gemeenten eerst ─────────────
  const gemeentenGesorteerd = sorteerGemeenten(regels.gemeenten, regels.medewerkers);

  // ── Stap 3: rooster initialiseren + slots verzamelen (volgorde bewaard) ──────
  const slots = [];
  gemeentenGesorteerd.forEach(([gemKey, gemeente]) => {
    rooster[gemKey] = {};
    log.push(`=== ${gemeente.naam} ===`);

    Object.entries(gemeente.diensten).forEach(([dienstId, dienst]) => {
      rooster[gemKey][dienstId] = rooster[gemKey][dienstId] || {};

      // Diensten met week_a_dagen/week_b_dagen zijn bi-wekelijks.
      // Als het huidige weektype geen dagen heeft (bijv. Lingewaard za/zo in week B),
      // gebruik [] zodat er geen onterechte slots worden aangemaakt.
      const dienstDagen = (type === 'A' && dienst.week_a_dagen)
        ? dienst.week_a_dagen
        : (type === 'B' && dienst.week_b_dagen)
        ? dienst.week_b_dagen
        : (dienst.week_a_dagen || dienst.week_b_dagen)
        ? []
        : (dienst.dagen || []);

      dienstDagen.forEach(dag => {
        if (dienstId === 'dagdienst' && (dag === 'za' || dag === 'zo')) {
          rooster[gemKey][dienstId][dag] = [];
          return;
        }
        // Vakantiestand: in een schoolvakantie draaien we ALLEEN late diensten. Een dagdienst
        // op een vakantiedag wordt overgeslagen (lege array, niet in `slots`) — exact hetzelfde
        // mechanisme als de weekend-skip hierboven, dus geen onterechte onderbezetting/conflict.
        if (dienstId === 'dagdienst' && vakantieDagen?.has(dag)) {
          rooster[gemKey][dienstId][dag] = [];
          // Lingewaard-substitutie: op hun vaste di/do draaien Haruun/Myrthe in een vakantieweek
          // een LATE i.p.v. de onderdrukte dagdienst. Push daarom een Lingewaard late_dienst-slot
          // op deze dag (hergebruik van de bestaande late-definitie en het generieke
          // startsWith('late_dienst')-pad). Init de late_dienst-map defensief eerst: de
          // dagdienst-iteratie kan voor de late_dienst-iteratie komen.
          if (gemKey === 'lingewaard') {
            const lateDef = gemeente.diensten.late_dienst;
            rooster[gemKey].late_dienst = rooster[gemKey].late_dienst || {};
            if (lateDef && !(dag in rooster[gemKey].late_dienst)) {
              rooster[gemKey].late_dienst[dag] = [];
              slots.push({ gemKey, gemeente, dienstId: 'late_dienst', dienst: lateDef, dag });
            }
          }
          return;
        }
        rooster[gemKey][dienstId][dag] = [];
        slots.push({ gemKey, gemeente, dienstId, dienst, dag });
      });
    });
  });

  // ── Hulpfunctie: vul één slot tot maxFill personen ────────────────────────
  // relaxWeekend=true: negeert weekend_rotatie (noodvulling als minimum niet gehaald)
  // caps: welke cap-set geldt (weekCaps voor minimum, weekCapsExtra voor Fase 2 extra's)
  // weigerLatePrefDag=true: late-voorkeur medewerkers worden NIET op dagdienst gezet (Fase 2).
  //   Late-voorkeur hoort laat te blijven; ze op dag-EXTRA's zetten verstoort hun late-schema
  //   en triggert via de rustregel verlies van een aangrenzende late dienst (bv. Mustafa −8u).
  function vulSlot({ gemKey, gemeente, dienstId, dag }, maxFill, relaxWeekend = false, caps = weekCaps, weigerLatePrefDag = false, klepTekort = null) {
    const ingepland = rooster[gemKey][dienstId][dag];
    if (ingepland.length >= maxFill) return;

    // null weeknummer schakelt de weekend-rotatie blokkering uit in isBeschikbaar
    const effectiefWeeknummer = relaxWeekend ? null : weeknummer;

    let kandidaten = Object.entries(regels.medewerkers)
      .filter(([mwKey, mw]) =>
        !mwKey.startsWith('_') &&
        isBeschikbaar(mwKey, mw, dag, gemKey, afwezigheid, type, effectiefWeeknummer, vakantieDagen)
      )
      .map(([mwKey, mw]) => ({ key: mwKey, ...mw }));

    if (dienstId === 'dagdienst') {
      kandidaten = kandidaten.filter(k => !k.geen_dagdienst);
      kandidaten = kandidaten.filter(k => !k.geen_dagdienst_dagen?.includes(dag));
      if (weigerLatePrefDag) kandidaten = kandidaten.filter(k => k.dienst_voorkeur !== 'late_dienst');
    }
    if (dienstId.startsWith('late_dienst') || dienstId === 'flexibel') {
      kandidaten = kandidaten.filter(k => !k.alleen_dagdienst || dag === 'za' || dag === 'zo');
      kandidaten = kandidaten.filter(k => !k.geen_late_dienst_dagen?.includes(dag));
    }

    // Spiegelt de blanket Arnhem-weekend-ban uit vulWeekendPaar: vast_rotatiepatroon-
    // medewerkers (Haruun/Myrthe = Lingewaard-specialisten) komen ook in de last-resort
    // individuele za/zo-vulling (4d) NIET in het Arnhem-weekend. Dag-gescopt zodat
    // weekdag-vulling (en Rheden-reparatie) ongemoeid blijft.
    if (gemKey === 'arnhem' && (dag === 'za' || dag === 'zo'))
      kandidaten = kandidaten.filter(k => !k.vast_rotatiepatroon);

    // Vast rotatiepatroon: medewerker is op voorkeur_gemeente-dienstdagen exclusief
    // beschikbaar voor die gemeente. Op andere dagen: normaal flexibel inzetbaar.
    kandidaten = kandidaten.filter(k => {
      if (!k.vast_rotatiepatroon || !k.voorkeur_gemeente || k.voorkeur_gemeente === gemKey) return true;
      const vorkGem = regels.gemeenten[k.voorkeur_gemeente];
      if (!vorkGem || vorkGem.actief === false) return true;
      const heeftDienstVandaag = Object.values(vorkGem.diensten).some(d => {
        const dagLijst = (type === 'A' && d.week_a_dagen) ? d.week_a_dagen
          : (type === 'B' && d.week_b_dagen) ? d.week_b_dagen
          : (d.week_a_dagen || d.week_b_dagen) ? []
          : (d.dagen || []);
        return dagLijst.includes(dag);
      });
      if (heeftDienstVandaag) {
        log.push(`[ROTATIE] ${k.naam} geblokkeerd voor ${gemeente.naam} op ${dag}: vast patroon ${vorkGem.naam}`);
      }
      return !heeftDienstVandaag;
    });

    // Bijvul-klep: beperk tot medewerkers nog ONDER hun maand-target en sorteer op
    // grootste maand-tekort eerst (eerlijk). Eligibility (isBeschikbaar, geen_dagdienst,
    // rotatie hierboven; CAO, rust, al-geplaatst in de fill-loop) blijft onverkort gelden.
    if (klepTekort) {
      kandidaten = kandidaten.filter(k => (klepTekort[k.key] || 0) > 0);
      kandidaten.sort((a, b) => (klepTekort[b.key] || 0) - (klepTekort[a.key] || 0));
    } else if (gemKey === 'rheden') {
      kandidaten.sort((a, b) => {
        const av = a.vast_weekend_gemeente === 'rheden' ? 0 : 1;
        const bv = b.vast_weekend_gemeente === 'rheden' ? 0 : 1;
        return av - bv || (latenTellerPerGem[a.key]?.rheden || 0) - (latenTellerPerGem[b.key]?.rheden || 0);
      });
    } else if (gemKey === 'lingewaard') {
      kandidaten.sort((a, b) => {
        const av = a.voorkeur_gemeente === 'lingewaard' ? 0 : 1;
        const bv = b.voorkeur_gemeente === 'lingewaard' ? 0 : 1;
        return av - bv;
      });
    } else {
      const isDag = dienstId === 'dagdienst';
      // Flex-afwisseling: een Flex-medewerker is aanspreekpunt van scholen en MOET binnen de week
      // afwisselen tussen dag- en late diensten. We sturen dit via de prioriteit op basis van wat
      // de persoon deze week al heeft (dagLaatTeller): heeft hij van dít type relatief weinig, dan
      // krijgt hij hoge prioriteit (1.5, net onder de toegewijde voorkeur); heeft hij al genoeg,
      // dan lage prioriteit (2.5). Zo ontstaat afwisseling met voldoende dagdiensten voor schoolbezoek.
      const voorkeurScore = (mw) => {
        const t = dagLaatTeller[mw.key] || { dag: 0, laat: 0 };
        if (isDag) {
          if (mw.alleen_dagdienst)                  return 0;
          if (mw.dienst_voorkeur === 'dagdienst')   return 1;
          if (mw.dienst_voorkeur === 'flexibel') {
            // Schoolaanspreekpunt krijgt voorrang voor zijn EERSTE dagdienst van de week (1.5) →
            // zo meestal wekelijks schoolbezoek (voorrang, geen harde garantie). Daarna concurreert hij gelijk (1.6) met gewone
            // Flex voor extra dagdiensten, zodat gewone Flex (Leroy) óók blijft afwisselen i.p.v.
            // door de aanspreekpunten verdrongen te worden. Geen voorrang bóven dagdienst-voorkeur
            // (1), dus de urenbalans van late-voorkeur medewerkers blijft intact.
            const aanspreekpunt = mw.schoolaanspreekpunt !== false;
            if (aanspreekpunt && t.dag === 0)  return 1.5;
            if (t.dag <= t.laat)               return 1.6;
            return 2.5;
          }
          if (mw.dienst_voorkeur === 'late_dienst') return 3;
          return 2;
        } else {
          if (mw.geen_dagdienst)                    return 0;
          if (mw.dienst_voorkeur === 'late_dienst') return 1;
          if (mw.dienst_voorkeur === 'flexibel')    return t.laat <= t.dag ? 1.5 : 2.5;
          if (mw.dienst_voorkeur === 'dagdienst')   return 3;
          return 2;
        }
      };
      // vast_rotatiepatroon-medewerkers (Haruun/Myrthe) zijn gecommitteerd aan hun voorkeur-
      // gemeente. Op een andere gemeente (Arnhem, weekdagen) krijgen ze de LAAGSTE prioriteit:
      // ze vullen hier alleen als flex met écht over budget. Anders zouden ze in een lichte
      // patroonweek een Arnhem-extra pakken die budget steelt van hun zware patroonweek.
      const patroonElders = (mw) => (mw.vast_rotatiepatroon && mw.voorkeur_gemeente && mw.voorkeur_gemeente !== gemKey) ? 1 : 0;
      // Onder Flex-kandidaten met gelijke voorkeurScore: wie het grootste tekort heeft van dít
      // dienst-type krijgt voorrang, zodat dag/late-diensten gelijkmatig over de Flex-mensen
      // verdeeld worden (geen enkele Flex draait bijna alleen dag of bijna alleen laat).
      const balansTie = (mw) => {
        if (mw.dienst_voorkeur !== 'flexibel') return 0;
        const t = dagLaatTeller[mw.key] || { dag: 0, laat: 0 };
        return isDag ? (t.dag - t.laat) : (t.laat - t.dag);
      };
      // Most-constrained-first voor DAGDIENSTEN: een Flex-medewerker met weinig weekdagen waarop
      // hij überhaupt dagdienst kan doen (Danny: alleen di/do/vr door ma-school + wo-vrij) krijgt
      // voorrang op de schaarse dag-slots boven Flex-mensen met meer dagopties (Bayram/Alper/Martijn:
      // ma-vr). Anders verliest de krapste de concurrentie en haalt hij zijn uren niet (−8u).
      const dagOpties = (mw) => {
        if (!isDag || mw.dienst_voorkeur !== 'flexibel') return 0;
        let n = 0;
        for (const d of ['ma', 'di', 'wo', 'do', 'vr']) {
          if (mw.vaste_vrije_dagen?.includes(d) || mw.kantoor_dagen?.includes(d) || (mw.school?.dag === d && !schoolVervalt(mw, vakantieDagen))) continue;
          n++;
        }
        return n; // minder opties → kleiner getal → eerder in de lijst
      };
      kandidaten.sort((a, b) =>
        patroonElders(a) - patroonElders(b) ||
        voorkeurScore(a) - voorkeurScore(b) ||
        dagOpties(a) - dagOpties(b) ||
        balansTie(a) - balansTie(b) ||
        (latenTellerPerGem[a.key]?.[gemKey] || 0) - (latenTellerPerGem[b.key]?.[gemKey] || 0)
      );
    }

    for (const k of kandidaten) {
      if (ingepland.length >= maxFill) break;

      const dienstUren = 8;
      const weekCap = caps ? (caps[k.key] ?? k.contract_uren) : k.contract_uren;
      if (urenTeller[k.key] + dienstUren > weekCap) {
        log.push(`[CAO] ${k.naam} overgeslagen: zou ${urenTeller[k.key] + dienstUren}u worden (cap: ${weekCap}u)`);
        continue;
      }

      // Rustregeling (bidirectioneel zodat de vul-volgorde niet uitmaakt):
      //   - dagdienst geblokkeerd als persoon de avond ervóór late dienst had
      //   - late dienst geblokkeerd als persoon de ochtend erná al dagdienst heeft
      if (dienstId === 'dagdienst') {
        const vDagIdx = DAGEN.indexOf(dag) - 1;
        if (vDagIdx >= 0 && heeftLateDienstOp(k.naam, DAGEN[vDagIdx], rooster)) {
          log.push(`[RUST] ${k.naam}: late dienst op ${DAGEN[vDagIdx]} → geen dagdienst ${dag}`);
          continue;
        }
      }
      if (dienstId.startsWith('late_dienst')) {
        const nDagIdx = DAGEN.indexOf(dag) + 1;
        if (nDagIdx < DAGEN.length && heeftDagdienstOp(k.naam, DAGEN[nDagIdx], rooster)) {
          log.push(`[RUST] ${k.naam}: dagdienst op ${DAGEN[nDagIdx]} → geen late dienst ${dag}`);
          continue;
        }
      }

      const alIngeplandErgens = Object.values(rooster).some(
        gemRooster => Object.values(gemRooster).some(
          dagmap => dagmap[dag] && dagmap[dag].includes(k.naam)
        )
      );
      if (alIngeplandErgens) {
        log.push(`[SKIP] ${k.naam} al ingepland op ${dag} (andere gemeente)`);
        continue;
      }

      // Max aaneengesloten gewerkte dagen. Bewust als LAATSTE horde: alle goedkopere
      // afwijzingen (cap, rust, dubbele plaatsing) zijn dan al gedaan, dus een [REEKS]-regel
      // in de log betekent echt "deze kandidaat mocht overal, behalve hierop".
      // Telt over week- én blokgrenzen heen, en in BEIDE richtingen: de vulvolgorde is niet
      // chronologisch, dus er kan al iets ná deze dag staan — zie telAaneengeslotenGewerkteDagenNa.
      const voor = reeksVoor(k, dag);
      const na = reeksNa(k, dag);
      if (voor + 1 + na > maxAaneengesloten) {
        log.push(`[REEKS] ${k.naam} overgeslagen ${dienstId} ${dag}: zou een reeks van ${voor + 1 + na} `
          + `aaneengesloten dagen geven (${voor} ervóór + deze + ${na} erná, max ${maxAaneengesloten})`);
        continue;
      }

      ingepland.push(k.naam);
      if (dienstId.startsWith('late_dienst')) {
        latenTellerPerGem[k.key][gemKey] = (latenTellerPerGem[k.key][gemKey] || 0) + 1;
        dagLaatTeller[k.key].laat++;
      } else if (dienstId === 'dagdienst') {
        dagLaatTeller[k.key].dag++;
      }
      urenTeller[k.key] += dienstUren;
      const label = relaxWeekend ? `[${gemeente.naam}·NOOD]` : `[${gemeente.naam}]`;
      log.push(`${label} ${k.naam} → ${dienstId} ${dag}`);
    }
  }

  // ── Stap 4: Minimumbezetting — weekdagen normaal, za/zo als paar ─────────
  // Za en zo worden ALTIJD tegelijk gevuld (één kandidaat werkt óf beide dagen óf geen):
  // dit garandeert hele weekenden en voorkomt dat iemand alleen za of alleen zo werkt.
  log.push('[FASE 1] Minimumbezetting...');
  const koppelGrootte = regels.instellingen?.koppel_grootte || 2;
  // Dunne closure-alias: geeft koppelGrootte door aan het geexporteerde berekenBezettingsDoel,
  // zodat alle call-sites hieronder ongewijzigd bezettingsDoel(d, max) blijven aanroepen.
  // Voor de formule zelf: zie de comment bij berekenBezettingsDoel — niet hier herhalen.
  const bezettingsDoel = (d, max) => berekenBezettingsDoel(d, max, koppelGrootte);

  // Groepeer za+zo-slots per (gemKey, dienstId) als paar
  const weekendParenMap = {};
  for (const slot of slots) {
    if (slot.dag !== 'za' && slot.dag !== 'zo') continue;
    const key = `${slot.gemKey}__${slot.dienstId}`;
    if (!weekendParenMap[key]) weekendParenMap[key] = { gemKey: slot.gemKey, gemeente: slot.gemeente, dienstId: slot.dienstId, dienst: slot.dienst };
    weekendParenMap[key][slot.dag + 'Slot'] = slot;
  }
  const weekendParen = Object.values(weekendParenMap);

  // Hulpfunctie: vul za+zo tegelijk — kandidaat moet minstens één benodigde dag kunnen
  // (verlof op één dag → behoudt de andere) en budget hebben voor de dag(en) die hij vult
  function vulWeekendPaar(paar, maxFill, relaxWeekend = false, caps = weekCaps) {
    const { gemKey, gemeente, dienstId } = paar;
    const zaRij = paar.zaSlot ? rooster[gemKey][dienstId]['za'] : null;
    const zoRij = paar.zoSlot ? rooster[gemKey][dienstId]['zo'] : null;
    const heeftZa = !!zaRij, heeftZo = !!zoRij;
    if ((!heeftZa || zaRij.length >= maxFill) && (!heeftZo || zoRij.length >= maxFill)) return;

    const effectiefWeeknummer = relaxWeekend ? null : weeknummer;

    let kandidaten = Object.entries(regels.medewerkers)
      .filter(([mwKey]) => !mwKey.startsWith('_'))
      .map(([mwKey, mw]) => ({
        key: mwKey,
        ...mw,
        _okZa: !heeftZa || isBeschikbaar(mwKey, mw, 'za', gemKey, afwezigheid, type, effectiefWeeknummer, vakantieDagen),
        _okZo: !heeftZo || isBeschikbaar(mwKey, mw, 'zo', gemKey, afwezigheid, type, effectiefWeeknummer, vakantieDagen),
      }))
      .filter(k => k._okZa || k._okZo);

    if (dienstId === 'dagdienst') {
      kandidaten = kandidaten.filter(k => !k.geen_dagdienst);
      if (heeftZa) kandidaten = kandidaten.filter(k => !k.geen_dagdienst_dagen?.includes('za'));
      if (heeftZo) kandidaten = kandidaten.filter(k => !k.geen_dagdienst_dagen?.includes('zo'));
    }
    if (dienstId.startsWith('late_dienst') || dienstId === 'flexibel') {
      if (heeftZa) kandidaten = kandidaten.filter(k => !k.geen_late_dienst_dagen?.includes('za'));
      if (heeftZo) kandidaten = kandidaten.filter(k => !k.geen_late_dienst_dagen?.includes('zo'));
    }
    // Vast-rotatiepatroon-medewerkers (Haruun/Myrthe = Lingewaard-specialisten) draaien geen
    // Arnhem-weekend: op hun patroon-weken doen ze Lingewaard za/zo, en op de andere weken
    // mogen ze niet als fill-to-max-vulling Arnhem-even overbezetten (moet op 3 blijven).
    if (gemKey === 'arnhem') kandidaten = kandidaten.filter(k => !k.vast_rotatiepatroon);

    kandidaten = kandidaten.filter(k => {
      if (!k.vast_rotatiepatroon || !k.voorkeur_gemeente || k.voorkeur_gemeente === gemKey) return true;
      const vorkGem = regels.gemeenten[k.voorkeur_gemeente];
      if (!vorkGem || vorkGem.actief === false) return true;
      const dagLijstVan = (d) => (type === 'A' && d.week_a_dagen) ? d.week_a_dagen
        : (type === 'B' && d.week_b_dagen) ? d.week_b_dagen
        : (d.week_a_dagen || d.week_b_dagen) ? [] : (d.dagen || []);
      const geblokkeerd = Object.values(vorkGem.diensten).some(d => {
        const dl = dagLijstVan(d);
        return (heeftZa && dl.includes('za')) || (heeftZo && dl.includes('zo'));
      });
      if (geblokkeerd) log.push(`[ROTATIE] ${k.naam} geblokkeerd voor ${gemeente.naam} weekend: vast patroon ${vorkGem.naam}`);
      return !geblokkeerd;
    });

    if (gemKey === 'rheden') {
      // Variabele 4e za/zo-plek op een Bayram-OFF-weekend: route naar de flex-pool
      // (instellingen.rheden_weekend_flex_volgorde) i.p.v. een willekeurige rotator.
      // Alleen rotatie-ON + beschikbare poolleden tellen mee (Kimmy, groep B, valt op
      // oneven weken vanzelf af via isBeschikbaar). Deterministisch op de ECHTE weeknummer
      // (niet effectiefWeeknummer, die is onder NOOD null): wk23→Mustafa, wk25→Alper.
      // Op even weken vullen J/B/L+Bayram (tier 0) alle 4 plekken → de flexer-tier wordt
      // nooit bereikt en het gedrag blijft ongewijzigd.
      const flexPool = regels.instellingen?.rheden_weekend_flex_volgorde || [];
      const flexEligible = flexPool.filter(fk => {
        const fmw = regels.medewerkers[fk];
        return fmw
          && isBeschikbaar(fk, fmw, 'za', 'rheden', afwezigheid, type, weeknummer, vakantieDagen)
          && isBeschikbaar(fk, fmw, 'zo', 'rheden', afwezigheid, type, weeknummer, vakantieDagen);
      });
      const flexer = flexEligible.length
        ? flexEligible[Math.floor((weeknummer - 1) / 2) % flexEligible.length]
        : null;
      const rhTier = (k) => k.vast_weekend_gemeente === 'rheden' ? 0 : (k.key === flexer ? 1 : 2);
      kandidaten.sort((a, b) =>
        rhTier(a) - rhTier(b) ||
        (latenTellerPerGem[a.key]?.rheden || 0) - (latenTellerPerGem[b.key]?.rheden || 0)
      );
    } else if (gemKey === 'lingewaard') {
      kandidaten.sort((a, b) =>
        (a.voorkeur_gemeente === 'lingewaard' ? 0 : 1) - (b.voorkeur_gemeente === 'lingewaard' ? 0 : 1)
      );
    } else {
      const isDag = dienstId === 'dagdienst';
      // Flex-afwisseling (zie vulSlot): Flex die dit type relatief weinig heeft → hoge prioriteit.
      // Weekenddiensten zijn laat; een Flex-medewerker die doordeweeks al dagdiensten draaide
      // krijgt zo voorrang voor de weekend-late, wat de afwisseling versterkt.
      const voorkeurScore = (mw) => {
        const t = dagLaatTeller[mw.key] || { dag: 0, laat: 0 };
        if (isDag) {
          if (mw.alleen_dagdienst) return 0;
          if (mw.dienst_voorkeur === 'dagdienst') return 1;
          if (mw.dienst_voorkeur === 'flexibel') return t.dag <= t.laat ? 1.5 : 2.5;
          if (mw.dienst_voorkeur === 'late_dienst') return 3;
          return 2;
        } else {
          if (mw.geen_dagdienst) return 0;
          if (mw.dienst_voorkeur === 'late_dienst') return 1;
          if (mw.dienst_voorkeur === 'flexibel') return t.laat <= t.dag ? 1.5 : 2.5;
          if (mw.dienst_voorkeur === 'dagdienst') return 3;
          return 2;
        }
      };
      // OPTIE A (Rotatie-ON-boost): wie deze week ON is in zijn weekend-rotatie
      // pakt zijn eigen weekend (Excel-gedrag) — topprioriteit vóór de
      // dienst_voorkeur-score. Zo belandt bv. Omar (dagdienst-voorkeur, groep B)
      // in zijn ON-week niet achteraan maar grijpt hij za+zo. Bewust de echte
      // `weeknummer` (niet effectiefWeeknummer): die is onder noodvulling null,
      // waardoor de pariteit verkeerd zou uitvallen.
      const rotatieOnDezeWeek = (mw) => {
        if (mw.weekend_rotatie !== true) return false;
        const groep = mw.weekend_groep || 'A';
        const isOff = groep === 'A' ? weeknummer % 2 === 0 : weeknummer % 2 !== 0;
        return !isOff;
      };
      kandidaten.sort((a, b) =>
        (rotatieOnDezeWeek(a) ? 0 : 1) - (rotatieOnDezeWeek(b) ? 0 : 1) ||
        voorkeurScore(a) - voorkeurScore(b) ||
        (latenTellerPerGem[a.key]?.[gemKey] || 0) - (latenTellerPerGem[b.key]?.[gemKey] || 0)
      );
    }

    const label = relaxWeekend ? `[${gemeente.naam}·NOOD]` : `[${gemeente.naam}]`;

    for (const k of kandidaten) {
      const zaVol = !heeftZa || zaRij.length >= maxFill;
      const zoVol = !heeftZo || zoRij.length >= maxFill;
      if (zaVol && zoVol) break;

      const zaNodig = heeftZa && !zaVol && !zaRij.includes(k.naam) && k._okZa;
      const zoNodig = heeftZo && !zoVol && !zoRij.includes(k.naam) && k._okZo;
      if (!zaNodig && !zoNodig) continue;

      // Budget: de te vullen dag(en) — za en/of zo — moeten samen binnen de cap passen
      const dienstUren = 8;
      const weekCap = caps ? (caps[k.key] ?? k.contract_uren) : k.contract_uren;
      const benodigdUren = (zaNodig ? dienstUren : 0) + (zoNodig ? dienstUren : 0);
      if (urenTeller[k.key] + benodigdUren > weekCap) {
        log.push(`[CAO] ${k.naam} overgeslagen weekend: ${urenTeller[k.key]}+${benodigdUren}u > ${weekCap}u`);
        continue;
      }

      if (zaNodig) {
        const alOpZa = Object.values(rooster).some(gr => Object.values(gr).some(dm => dm['za']?.includes(k.naam)));
        if (alOpZa) { log.push(`[SKIP] ${k.naam} al op za`); continue; }
      }
      if (zoNodig) {
        const alOpZo = Object.values(rooster).some(gr => Object.values(gr).some(dm => dm['zo']?.includes(k.naam)));
        if (alOpZo) { log.push(`[SKIP] ${k.naam} al op zo`); continue; }
      }

      // Max aaneengesloten gewerkte dagen — ANALYTISCH, niet twee keer de teller. Za en zo
      // grenzen aan elkaar, dus als we ze allebei vullen vormen ze ÉÉN reeks: de dagen vóór
      // zaterdag, de twee weekenddagen zelf, en de dagen ná zondag. De teller zou dat niet
      // zien, want op het moment van de check staat geen van beide nog in het rooster.
      //   voor(za) wordt niet beïnvloed door zondag (die ligt erná),
      //   na(zo)   wordt niet beïnvloed door zaterdag (die ligt ervóór),
      // dus beide zijn op de levende stand correct te meten zonder proefplaatsing.
      let reeksWeekend = 0;
      let welke = '';
      if (zaNodig && zoNodig) { reeksWeekend = reeksVoor(k, 'za') + 2 + reeksNa(k, 'zo'); welke = 'za+zo'; }
      else if (zaNodig)       { reeksWeekend = reeksVoor(k, 'za') + 1 + reeksNa(k, 'za'); welke = 'za'; }
      else if (zoNodig)       { reeksWeekend = reeksVoor(k, 'zo') + 1 + reeksNa(k, 'zo'); welke = 'zo'; }
      // Te lang → de HELE kandidaat valt af, ook de andere dag. Halve weekenden toestaan zou
      // de hele-weekenden-garantie van deze functie breken (zie stap 4). Omdat za+zo als één
      // reeks gemeten wordt, volgt die volledige weigering vanzelf uit één check.
      if (reeksWeekend > maxAaneengesloten) {
        log.push(`[REEKS] ${k.naam} overgeslagen weekend ${dienstId} (${welke}): zou een reeks van `
          + `${reeksWeekend} aaneengesloten dagen geven (max ${maxAaneengesloten}) → hele weekend geweigerd`);
        continue;
      }

      const gePlaatst = [];
      let uren = 0;
      if (zaNodig) { zaRij.push(k.naam); uren += dienstUren; gePlaatst.push('za'); }
      if (zoNodig) { zoRij.push(k.naam); uren += dienstUren; gePlaatst.push('zo'); }
      urenTeller[k.key] += uren;
      if (dienstId.startsWith('late_dienst')) {
        latenTellerPerGem[k.key][gemKey] = (latenTellerPerGem[k.key][gemKey] || 0) + gePlaatst.length;
        dagLaatTeller[k.key].laat += gePlaatst.length;
      } else if (dienstId === 'dagdienst') {
        dagLaatTeller[k.key].dag += gePlaatst.length;
      }
      log.push(`${label} ${k.naam} → ${dienstId} ${gePlaatst.join('+')} (heel weekend)`);
    }
  }

  // 4a: weekenden EERST (meest beperkte slots — alleen de ON-groep is beschikbaar).
  // Dit reserveert weekend-rotatie medewerkers voor hun ON-weekend vóórdat weekdag-diensten
  // hun (vaak krappe) budget opmaken. Zonder dit at een Rheden-vr-dienst Emre's 16u straat-budget
  // op (32u − kantoor di − school ma), waardoor hij in zijn ON-week geen heel weekend meer kon draaien.
  // "Most constrained first": weekenddagen hebben een kleine kandidatenpool, weekdagen een grote.
  log.push('[FASE 1a] Weekenden eerst (za+zo als paar)...');
  // Vul tot de BOVENGRENS (bezettingsDoel true) zodat oneven_toegestaan-weekenden hier al
  // tot beschikbaar-max vullen vóórdat de weekdagen het budget opmaken (anders komt de 4e
  // Arnhem-plek pas in Fase 2 aan bod, als de pool al budget-leeg is). "Vullen-tot-beschikbaar":
  // een dunne (even) kant blijft vanzelf op de min, een drukke (oneven) kant haalt 4.
  for (const [gemKey] of gemeentenGesorteerd) {
    for (const paar of weekendParen) {
      if (paar.gemKey !== gemKey) continue;
      vulWeekendPaar(paar, bezettingsDoel(paar.dienst, true));
    }
  }

  const isWeekdag = (dag) => dag !== 'za' && dag !== 'zo';

  // 4b: weekdagen (ma-vr). Dagdienst EERST, dan late diensten.
  // Dagdienst heeft een kleine kandidatenpool (alleen_dagdienst-specialisten + enkele flex);
  // late dienst heeft een ruime pool. Door dagdienst eerst te vullen komen dagdienst-voorkeur
  // medewerkers (Danny, Martijn) op hun dagdienst terecht i.p.v. opgebrand te raken op late
  // diensten — wat anders een leeg dagdienst-vr-slot in de krappe laatste week veroorzaakte.
  log.push('[FASE 1b] Weekdagen — dagdienst eerst...');
  for (const [gemKey] of gemeentenGesorteerd) {
    for (const slot of slots) {
      if (slot.gemKey !== gemKey || !isWeekdag(slot.dag) || slot.dienstId !== 'dagdienst') continue;
      vulSlot(slot, bezettingsDoel(slot.dienst, false));
    }
  }

  log.push('[FASE 1b] Weekdagen — late/overige diensten...');
  for (const [gemKey] of gemeentenGesorteerd) {
    for (const slot of slots) {
      if (slot.gemKey !== gemKey || !isWeekdag(slot.dag) || slot.dienstId === 'dagdienst') continue;
      vulSlot(slot, bezettingsDoel(slot.dienst, false));
    }
  }

  // ── Stap 4c: Weekend noodvulling — minimum gehaald ondanks rotatie ────────
  // Als het minimum niet gehaald is, worden OFF-groep medewerkers als noodoplossing ingezet.
  // Nog steeds als paar (za+zo), zodat geen halve weekenden ontstaan.
  log.push('[FASE 1b] Weekend noodvulling...');
  for (const paar of weekendParen) {
    const minFill = bezettingsDoel(paar.dienst, false);
    const zaLen = paar.zaSlot ? (rooster[paar.gemKey][paar.dienstId]['za']?.length || 0) : minFill;
    const zoLen = paar.zoSlot ? (rooster[paar.gemKey][paar.dienstId]['zo']?.length || 0) : minFill;
    if (zaLen >= minFill && zoLen >= minFill) continue;
    log.push(`[NOOD] ${paar.gemeente.naam} ${paar.dienstId} za/zo: minimum niet gehaald, relaxeer weekend-rotatie`);
    vulWeekendPaar(paar, minFill, false);
  }

  // ── Stap 4d: Conflict-reparatie — individueel za/zo als last resort ─────────
  // Als na paarvulling het minimum NOG niet gehaald is (bijv. niemand heeft budget voor beide
  // dagen), vul dan individueel op om een conflictsituatie te voorkomen. Geeft in zelden
  // gevallen toch een halve weekend, maar voorkomt dat een dienst onbemand blijft.
  log.push('[FASE 1c] Conflict-reparatie (individueel za/zo last resort)...');
  for (const slot of slots) {
    if (slot.dag !== 'za' && slot.dag !== 'zo') continue;
    const minFill = bezettingsDoel(slot.dienst, false);
    if ((rooster[slot.gemKey][slot.dienstId][slot.dag] || []).length >= minFill) continue;
    log.push(`[LAST-RESORT] ${slot.gemeente.naam} ${slot.dienstId} ${slot.dag}: paar niet volledig, individueel aanvullen`);
    vulSlot(slot, minFill, false);
  }

  // ── Stap 5: Fase 2 — extra koppels tot max_koppels ────────────────────────
  // Extra's gebruiken de KRAPPERE weekCapsExtra (gelijkmatig tempo) i.p.v. weekCaps.
  // Zo souperen extra koppels in vroege weken niet het budget op dat latere weken nodig
  // hebben voor hun MINIMUM-bezetting. Over 4 weken wordt iedereen alsnog op ±0u gevuld.
  const extraCaps = weekCapsExtra || weekCaps;
  log.push('[FASE 2] Extra koppels (throttled budget)...');
  // Eerst de dagdienst-extra's, daarna de late-extra's — beide eerlijk gespreid (grootste tekort
  // eerst) i.p.v. de slots in vaste lijst-volgorde (ma->vr) leeg te vullen. We kiezen telkens het
  // slot dat het VERST onder zijn doel zit en vullen dat één plek bij, herberekenen, herhalen. Zo
  // verdelen extra koppels zich eerlijk over de week i.p.v. dat maandag het budget opsoupeert en
  // vrijdag mager blijft. De dagdienst-groep blijft VÓÓR de late-groep (zoals Fase 1b), zodat Flex
  // genoeg dagdiensten (schoolbezoek) houdt. vulSlot zelf (budget/rust/caps/eligibility) blijft
  // ongewijzigd — enkel de VOLGORDE waarin slots aan bod komen verandert. Determinisme: gelijke
  // tekorten breken op de oorspronkelijke slot-index (strikt >, dus laagste index = oude ma->vr).
  const vulGroepEerlijk = (groepSlots, maxVan, weigerLatePrefDag = false) => {
    const items = groepSlots.map(slot => ({ slot, max: maxVan(slot) }));
    const huidig = (slot) => rooster[slot.gemKey][slot.dienstId][slot.dag].length;
    const vast = new Set(); // slots die niet verder konden (budget/eligibility op) → niet herkiezen
    for (;;) {
      let keuze = null;
      let besteTekort = 0;
      for (const it of items) {
        if (vast.has(it)) continue;
        const tekort = it.max - huidig(it.slot);
        if (tekort > besteTekort) { besteTekort = tekort; keuze = it; }
      }
      if (!keuze) break; // geen slot meer onder zijn doel
      const voor = huidig(keuze.slot);
      vulSlot(keuze.slot, voor + 1, false, extraCaps, weigerLatePrefDag); // hoogstens één plek erbij
      if (huidig(keuze.slot) === voor) vast.add(keuze); // kon niet vullen → bevries
    }
  };

  // Dagdienst-extra-groep eerst (weigerLatePrefDag: late-voorkeur niet op dag-extra's).
  vulGroepEerlijk(
    slots.filter(s => s.dag !== 'za' && s.dag !== 'zo' && s.dienstId === 'dagdienst'),
    (slot) => bezettingsDoel(slot.dienst, true),
    true
  );
  // Late-extra-groep daarna (alle overige weekdag-slots). Anders dan de dagdienst-ronde
  // sorteren we de late-slots op LATERE weekdag eerst (vr→ma, DAGEN-index aflopend): bij een
  // GELIJK tekort wint zo — via de bestaande "eerst-gescande wint"-tie-break — de latere dag,
  // zodat late-overschot naar het eind van de week schuift i.p.v. naar maandag te klonteren.
  vulGroepEerlijk(
    slots
      .filter(s => s.dag !== 'za' && s.dag !== 'zo' && s.dienstId !== 'dagdienst')
      .sort((a, b) => DAGEN.indexOf(b.dag) - DAGEN.indexOf(a.dag)),
    (slot) => {
      const standaardMax = bezettingsDoel(slot.dienst, true);
      // Schoolvakantie + Arnhem: late schaalt op naar 4 koppels (8 personen); elders standaard.
      const isVakantieArnhemLate = slot.gemKey === 'arnhem' && vakantieDagen?.has(slot.dag);
      return isVakantieArnhemLate ? 4 * koppelGrootte : standaardMax;
    }
  );
  for (const paar of weekendParen) {
    const max = bezettingsDoel(paar.dienst, true);
    vulWeekendPaar(paar, max, false, extraCaps);
  }

  // ── Stap 5b: Gerichte dagdienst-bijvul-klep — alleen Arnhem weekdag-dagdienst ──────
  // Voegt ONDER-TARGET, BEREIKBARE medewerkers toe als EXTRA (oneven) dagdienst-bezetting
  // tot hun cumulatieve maand-target — buffer voor verlof/ziekte. Append-only (vulSlot voegt
  // enkel toe, verdringt niemand). CAO hard via extraCaps (weekCapsExtra). Rust (late->dag) en
  // rotatie blijven gelden via hergebruik van de vulSlot-eligibility. Weekend/Lingewaard/Rheden
  // ongemoeid (filter op gemKey === 'arnhem' + weekdag + dagdienst).
  if (maandTekort) {
    log.push('[FASE 2b] Dagdienst-bijvul-klep (onder-target, bereikbaar, append-only)...');
    const dd = regels.gemeenten.arnhem?.diensten?.dagdienst;
    // Nood-plek tot 5 (niet 6): het dienst-maximum (bezettingsDoel) is de normale 4; +1 geeft één
    // oneven nood-plek, nooit een heel extra koppel (dus hoogstens 5). Ontbreekt de dagdienst in
    // deze omgeving, dan blijft de oude vangnet-waarde (koppelGrootte) staan — klepSlots is dan
    // toch leeg, dus de lus draait niet.
    const noodCap = (dd ? bezettingsDoel(dd, true) : koppelGrootte) + 1;
    // Eerlijk gespreid (grootste tekort eerst), net als vulGroepEerlijk in de late-ronde, i.p.v.
    // greedy ma->vr: kies telkens het Arnhem-weekdag-dagdienst-slot dat het VERST onder noodCap
    // zit, vul daar 1 plek bij, herbereken, herhaal — zo verdelen nood-plekken zich over de week
    // (bv. ma=5/wo=5) i.p.v. op maandag te stapelen. vulGroepEerlijk wordt NIET hergebruikt omdat
    // die de maandTekort-kandidaatfilter (6e vulSlot-arg) niet doorgeeft; deze kleine lus is
    // identiek van vorm maar behoudt dat onder-target/bereikbaar-filter (= de gate ongemoeid).
    const klepSlots = slots.filter(s => s.gemKey === 'arnhem' && isWeekdag(s.dag) && s.dienstId === 'dagdienst');
    const klepHuidig = (s) => rooster[s.gemKey][s.dienstId][s.dag].length;
    const klepVast = new Set(); // slots die niet verder konden (budget/eligibility op) -> niet herkiezen
    for (;;) {
      let keuze = null;
      let besteTekort = 0;
      for (const s of klepSlots) {
        if (klepVast.has(s)) continue;
        const tekort = noodCap - klepHuidig(s);
        if (tekort > besteTekort) { besteTekort = tekort; keuze = s; } // strikt > -> tie = laagste index (ma->vr)
      }
      if (!keuze) break; // geen slot meer onder noodCap
      const voor = klepHuidig(keuze);
      vulSlot(keuze, voor + 1, false, extraCaps, false, maandTekort); // 1 plek erbij; maandTekort-filter intact
      if (klepHuidig(keuze) === voor) klepVast.add(keuze); // kon niet vullen -> bevries
    }
  }

  // ── Stap 5c (PROTOTYPE): mini-herschik — rustregel-klem oplossen ──────────
  // Voor iedere medewerker ONDER zijn week-straatbudget (weekCaps) met een
  // beschikbare weekdag die ALLEEN door de rustregel geblokkeerd wordt:
  //   1. vind de eigen aangrenzende veroorzaker (late dienst dag ervoor bij een
  //      dag-slot, dagdienst dag erna bij een laat-slot)
  //   2. wissel die veroorzaker op ZIJN EIGEN dag van diensttype (dag<->laat),
  //      mits doeldienst plek heeft, bron-slot op/boven minimum blijft (incl.
  //      oneven/koppel-regels) en alle beschikbaarheids- en rustregels geldig
  //      blijven
  //   3. plaats de medewerker daarna op de vrijgekomen dag (+8u)
  // Deterministisch: alfabetisch op naam, dagen ma→vr, max 1 herschik p.p.p.w.
  // Alleen de eigen diensten van de medewerker bewegen; anderen blijven exact.
  // WHITELIST (optie b): stap 5c draait UITSLUITEND voor medewerkers in
  // herschikWhitelist — gevuld door de herpass in genereerMaandrooster met de
  // medewerkers die fase 1 met een tekort afsloten. Zonder whitelist: no-op,
  // gedrag byte-identiek aan de huidige engine.
  if (herschikWhitelist && herschikWhitelist.size > 0) {
    const WEEKDAGEN_HS = ['ma', 'di', 'wo', 'do', 'vr'];
    const maxCapVan = (d) => bezettingsDoel(d, true); // zelfde als Fase 2-weekdagvulling
    const rustOk = (naam, dienstId, dg, rstr = rooster) => {
      if (dienstId === 'dagdienst') {
        const v = DAGEN.indexOf(dg) - 1;
        if (v >= 0 && heeftLateDienstOp(naam, DAGEN[v], rstr)) return false;
      }
      if (dienstId.startsWith('late_dienst')) {
        const n = DAGEN.indexOf(dg) + 1;
        if (n < DAGEN.length && heeftDagdienstOp(naam, DAGEN[n], rstr)) return false;
      }
      return true;
    };
    // Hoe lang wordt de reeks rónd `dg` als deze medewerker daar (ook) werkt? Zelfde
    // rooster-override als rustOk hierboven: `rstr` is het levende rooster, of de proef-KLOON
    // uit de donor-swap. reeksVoor/reeksNa (r429-430) kunnen dat niet — die zitten vast aan
    // historieWeken, en dat wijst naar het levende rooster.
    // De +1 is de dag zelf. Die telt in geen van beide tellers mee, dus de uitkomst is dezelfde
    // of `dg` nu al in `rstr` staat (kloon: wél) of nog niet (live: nog niet).
    const reeksRond = (mw, dg, rstr = rooster) => {
      const weken = rstr === rooster
        ? historieWeken
        : [...voorafgaandeWeken, { weeknummer, rooster: rstr, schoolweekType: type }];
      return telAaneengeslotenGewerkteDagen(weken, mw, weeknummer, dg, reeksCtx)
        + 1
        + telAaneengeslotenGewerkteDagenNa(weken, mw, weeknummer, dg, reeksCtx);
    };
    // slotReden: eerste regel die mwKey in slot s blokkeert, of null als toegestaan.
    // PUUR diagnostisch single-source; magInSlot is de byte-identieke boolean-wrapper
    // (slotReden===null) die de plaatsing ONGEWIJZIGD gebruikt — geen gedragswijziging.
    const slotReden = (mwKey, m, s) => {
      if (!isBeschikbaar(mwKey, m, s.dag, s.gemKey, afwezigheid, type, weeknummer, vakantieDagen)) return 'niet-beschikbaar';
      if (s.dienstId === 'dagdienst' && m.geen_dagdienst) return 'geen_dagdienst';
      if (s.dienstId === 'dagdienst' && m.geen_dagdienst_dagen?.includes(s.dag)) return 'geen_dagdienst_dag';
      if (s.dienstId.startsWith('late_dienst') || s.dienstId === 'flexibel') {
        if (m.alleen_dagdienst && s.dag !== 'za' && s.dag !== 'zo') return 'alleen_dagdienst';
        if (m.geen_late_dienst_dagen?.includes(s.dag)) return 'geen_late_dienst_dag';
      }
      if (m.vast_rotatiepatroon && m.voorkeur_gemeente && m.voorkeur_gemeente !== s.gemKey) {
        const vorkGem = regels.gemeenten[m.voorkeur_gemeente];
        if (vorkGem && vorkGem.actief !== false) {
          const blok = Object.values(vorkGem.diensten).some(d => {
            const dl = (type === 'A' && d.week_a_dagen) ? d.week_a_dagen
              : (type === 'B' && d.week_b_dagen) ? d.week_b_dagen
              : (d.week_a_dagen || d.week_b_dagen) ? [] : (d.dagen || []);
            return dl.includes(s.dag);
          });
          if (blok) return 'rotatiepatroon-blokkeert';
        }
      }
      return null;
    };
    const magInSlot = (mwKey, m, s) => slotReden(mwKey, m, s) === null;
    const vindEigenDienst = (naam, dg, pred) => {
      for (const [gemKey, gr] of Object.entries(rooster))
        for (const [id, dm] of Object.entries(gr))
          if (pred(id) && dm[dg]?.includes(naam)) return { gem: gemKey, id, dag: dg };
      return null;
    };
    // Clone die alle dag-arrays kopieert (gemeente/dienst-objecten gedeeld), zodat een
    // proef-mutatie (validate-then-commit) de live rooster niet raakt. Klein corpus -> goedkoop.
    const cloneRooster = (r) =>
      Object.fromEntries(Object.entries(r).map(([g, gr]) =>
        [g, Object.fromEntries(Object.entries(gr).map(([id, dm]) =>
          [id, Object.fromEntries(Object.entries(dm).map(([d, arr]) => [d, arr.slice()]))]))]));

    // herschikWhitelist is nu Map<key, fase1-afwijking>; sorteer op grootste tekort eerst.
    const mwLijst = Object.entries(regels.medewerkers)
      .filter(([k]) => !k.startsWith('_') && herschikWhitelist.has(k))
      .sort((a, b) => {
        const da = herschikWhitelist.get(a[0]) ?? 0;
        const db = herschikWhitelist.get(b[0]) ?? 0;
        if (da !== db) return da - db;               // grootste tekort (meest negatief) eerst
        return a[1].naam.localeCompare(b[1].naam);   // stabiele tiebreak -> determinisme
      });

    for (const [mwKey, m] of mwLijst) {
      const cap = weekCaps ? (weekCaps[mwKey] ?? m.contract_uren) : m.contract_uren;
      let klaar = false;

      for (const dag of WEEKDAGEN_HS) {
        if (klaar) break;
        if (urenTeller[mwKey] + 8 > cap) break; // niet (meer) onder budget

        const alIngepland = Object.values(rooster).some(gr =>
          Object.values(gr).some(dm => dm[dag]?.includes(m.naam)));
        if (alIngepland) continue;

        for (const s of slots) {
          if (klaar) break;
          if (s.dag !== dag) continue;
          const rij = rooster[s.gemKey][s.dienstId][dag];
          if (rij.length >= maxCapVan(s.dienst)) continue;
          if (!magInSlot(mwKey, m, s)) continue;
          if (rustOk(m.naam, s.dienstId, dag)) continue; // niet rust-geklemd → hier niets doen

          // Vanaf hier is s een ECHTE reddingskandidaat (bereikbaar, onder cap, rust-geklemd).
          // Elke afwijzing hieronder gaat PUUR naar herschikTrace (zacht veld) — nooit naar een hard veld.
          const doelLbl = `${s.gemKey}/${s.dienstId}/${dag}`;

          // veroorzaker: eigen aangrenzende dienst die de rustblokkade geeft
          let bron, doelPred, wisselLabel;
          if (s.dienstId === 'dagdienst') {
            const vd = DAGEN[DAGEN.indexOf(dag) - 1];
            bron = vindEigenDienst(m.naam, vd, id => id.startsWith('late_dienst'));
            doelPred = (id) => id === 'dagdienst';
            wisselLabel = 'laat→dag';
          } else {
            const nd = DAGEN[DAGEN.indexOf(dag) + 1];
            bron = vindEigenDienst(m.naam, nd, id => id === 'dagdienst');
            doelPred = (id) => id.startsWith('late_dienst');
            wisselLabel = 'dag→laat';
          }
          if (!bron) {
            herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: rust-klem (${wisselLabel}), geen eigen aangrenzende bron-dienst → geen herschik`);
            continue;
          }

          const bronLbl = `${bron.gem}/${bron.id}/${bron.dag}`;
          const bronDienst = regels.gemeenten[bron.gem].diensten[bron.id];
          const bronRij = rooster[bron.gem][bron.id][bron.dag];
          const naVerwijdering = bronRij.length - 1;
          const bronMin = bezettingsDoel(bronDienst, false);
          if (naVerwijdering < bronMin) {
            // ── Vorm-(i) donor-swap: telling-neutraal op bron EN t; alleen s +1 ──────
            // Alleen voor een oneven_toegestaan doel-slot s. Het enige clean Guard-1-punt
            // (blok-13/Haruun) heeft een EVEN doel (late_dienst_dw) -> gate faalt -> donor-
            // pad niet betreden -> alle 13 clean-blokken byte-identiek.
            if (s.dienst.oneven_toegestaan) {
              const bronSlotKey = { gemKey: bron.gem, dienstId: bron.id, dag: bron.dag };
              for (const t of slots) {
                if (t.dag !== bron.dag || !doelPred(t.dienstId)) continue;
                if (slotReden(mwKey, m, t) !== null) continue;          // X mag op t? (rule-only, geen cap)
                const tRijLive = rooster[t.gemKey][t.dienstId][t.dag];
                for (const donor of tRijLive.slice()) {                 // deterministisch: insert-volgorde
                  if (donor === m.naam) continue;
                  const dEntry = Object.entries(regels.medewerkers)
                    .find(([k, mw]) => !k.startsWith('_') && mw.naam === donor);
                  if (!dEntry) continue;
                  const [dKey, dMw] = dEntry;
                  if (slotReden(dKey, dMw, bronSlotKey) !== null) continue;   // D mag op bron?

                  // Valideer op de RESULTERENDE staat (clone), zonder live te muteren.
                  const na = cloneRooster(rooster);
                  na[bron.gem][bron.id][bron.dag] =
                    na[bron.gem][bron.id][bron.dag].filter(n => n !== m.naam).concat(donor);
                  na[t.gemKey][t.dienstId][t.dag] =
                    na[t.gemKey][t.dienstId][t.dag].filter(n => n !== donor).concat(m.naam);
                  na[s.gemKey][s.dienstId][s.dag] =
                    na[s.gemKey][s.dienstId][s.dag].concat(m.naam);

                  const okTelling =
                    // GEEN t-max-check: swap is telling-neutraal op t (donor eruit/X erin); de bijvul-klep mag t
                    // legitiem > maxCapVan vullen -- een max-check zou de swap dan onterecht weigeren.
                    na[bron.gem][bron.id][bron.dag].length >= bronMin &&
                    na[s.gemKey][s.dienstId][s.dag].length <= maxCapVan(s.dienst);
                  const okRust =
                    rustOk(donor, bron.id, bron.dag, na) &&            // D op nieuwe bron
                    rustOk(m.naam, t.dienstId, t.dag, na) &&           // X op nieuwe t
                    rustOk(m.naam, s.dienstId, dag, na);               // X op s (bron is X al kwijt)
                  // Max aaneengesloten: gemeten op de KLOON, waar de swap én de nieuwe dag al in
                  // zitten. Zo wordt er nooit gecommit wat daarna geweigerd zou moeten worden.
                  // Alleen X wordt gemeten: de donor gaat van t naar bron en die liggen op
                  // dezelfde dag (r1298), dus voor hem verandert er geen gewerkte dag.
                  const reeksNaSwap = reeksRond(m, dag, na);
                  const okReeks = reeksNaSwap <= maxAaneengesloten;
                  if (okTelling && okRust && !okReeks) {
                    herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: donor-swap via ${donor} zou een reeks van ${reeksNaSwap} aaneengesloten dagen geven (max ${maxAaneengesloten}) → afgewezen [REEKS]`);
                  }
                  if (!okTelling || !okRust || !okReeks) continue;

                  // COMMIT: exact dezelfde swap op de live rooster.
                  bronRij.splice(bronRij.indexOf(m.naam), 1); bronRij.push(donor);
                  tRijLive.splice(tRijLive.indexOf(donor), 1); tRijLive.push(m.naam);
                  rij.push(m.naam);
                  urenTeller[mwKey] += 8;
                  log.push(`[HERSCHIK] ${m.naam}: donor-swap (${wisselLabel}) bron ${bronLbl} <-> ${t.gemKey}/${t.dienstId}/${t.dag}; donor ${donor} -> bron; nieuw ${doelLbl} (+8u -> ${urenTeller[mwKey]}u)`);
                  herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: GESLAAGD donor-swap bron ${bronLbl} <-> ${t.gemKey}/${t.dienstId}/${t.dag} via ${donor} (+8u)`);
                  klaar = true;
                  break;
                }
                if (klaar) break;
              }
            }
            if (klaar) break;                                          // donor-swap geslaagd -> stop deze persoon
            herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: bron ${bronLbl} zou onder min zakken (na=${naVerwijdering} < min=${bronMin}) → afgewezen [Guard 1]`);
            continue;
          }
          if (!bronDienst.oneven_toegestaan && naVerwijdering % koppelGrootte !== 0) {
            herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: bron ${bronLbl} zou koppel breken (na=${naVerwijdering} % ${koppelGrootte} ≠ 0) → afgewezen`);
            continue;
          }

          for (const t of slots) {
            if (t.dag !== bron.dag || !doelPred(t.dienstId)) continue;
            const tLbl = `${t.gemKey}/${t.dienstId}/${t.dag}`;
            const tRij = rooster[t.gemKey][t.dienstId][t.dag];
            if (tRij.length >= maxCapVan(t.dienst)) {
              herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: wissel-doel ${tLbl} vol (${tRij.length}/${maxCapVan(t.dienst)}) → volgend doel`);
              continue;
            }
            const tReden = slotReden(mwKey, m, t);
            if (tReden) {
              herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: wissel-doel ${tLbl} niet toegestaan (${tReden}) → volgend doel`);
              continue;
            }

            // probeer de wissel; bij rust-schending of een te lange reeks direct terugdraaien
            const idx = bronRij.indexOf(m.naam);
            bronRij.splice(idx, 1);
            tRij.push(m.naam);
            // Max aaneengesloten rond de NIEUWE dag. De wissel zelf is dag-neutraal
            // (t.dag === bron.dag), dus meten vóór of ná de wissel geeft hetzelfde; hier meten
            // hergebruikt de terugdraai die er voor de rustregel al staat.
            const reeksNaWissel = reeksRond(m, dag);
            const rustFout = !rustOk(m.naam, t.dienstId, t.dag) || !rustOk(m.naam, s.dienstId, dag);
            const reeksFout = reeksNaWissel > maxAaneengesloten;
            if (rustFout || reeksFout) {
              tRij.pop();
              bronRij.splice(idx, 0, m.naam);
              const reden = rustFout
                ? 'schendt rust'
                : `zou een reeks van ${reeksNaWissel} aaneengesloten dagen geven (max ${maxAaneengesloten})`;
              herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: wissel bron ${bronLbl} → ${tLbl} ${reden} → teruggedraaid`);
              continue;
            }

            rij.push(m.naam);
            urenTeller[mwKey] += 8;
            log.push(`[HERSCHIK] ${m.naam}: ${wisselLabel} ${bron.gem}/${bron.id}/${bron.dag} → ${t.gemKey}/${t.dienstId}/${t.dag}; nieuw geplaatst ${s.gemKey}/${s.dienstId}/${dag} (+8u → ${urenTeller[mwKey]}u)`);
            herschikTrace.push(`[HERSCHIK-TRACE] ${m.naam} ${dag} doel ${doelLbl}: GESLAAGD ${wisselLabel} bron ${bronLbl} → ${tLbl} (+8u → ${urenTeller[mwKey]}u)`);
            klaar = true;
            break;
          }
        }
      }
    }
  }

  // ── Stap 5d (observability, Fase 1): onder-min/surplus per slot ─────────────
  // PUUR diagnostisch. Verse array uit de DEFINITIEVE rooster-stand (na alle vul-/
  // herschik-passes, vóór de conflict-telling); muteert niets, voedt geen plaatsing/
  // teller/conflict. Slot-volgorde = stabiele insert-volgorde uit Stap 3 → deterministisch.
  const bezettingsMap = slots.map((s) => {
    const bezet = (rooster[s.gemKey][s.dienstId][s.dag] || []).length;
    const min = bezettingsDoel(s.dienst, false);
    const max = bezettingsDoel(s.dienst, true);
    return { gemKey: s.gemKey, dienstId: s.dienstId, dag: s.dag, bezet, min, max, onderMin: Math.max(0, min - bezet), surplus: Math.max(0, bezet - min) };
  });

  // ── Stap 6: conflict checks ───────────────────────────────────────────────
  for (const slot of slots) {
    const { gemKey, gemeente, dienstId, dienst, dag } = slot;
    const ingepland = rooster[gemKey][dienstId][dag];
    // Onderbezet = minder personen dan het bezettingsdoel — één bron van waarheid met de
    // vulpassen. Het conflict rapporteert in PERSONEN (bezet/nodig), dezelfde eenheid als
    // het doel, zodat melding en bezettingsdoel niet uiteen kunnen lopen.
    const bezet = ingepland.length;
    const nodig = berekenBezettingsDoel(dienst, false, koppelGrootte);
    if (bezet < nodig) {
      conflicten.push({
        gemeente: gemeente.naam,
        gemeenteKey: gemKey,
        dienst: dienstId,
        dag,
        bezet,
        nodig,
        ingepland,
        ernst: ingepland.length <= 1 ? 'rood' : 'oranje',
        weeknummer,
        weekType: type,
      });
      log.push(`[WARN] ${gemeente.naam} ${dienstId} ${dag}: ${bezet}/${nodig} personen`);
    }
    if (!dienst.oneven_toegestaan && ingepland.length % koppelGrootte !== 0) {
      log.push(`[LET OP] ${gemeente.naam} ${dienstId} ${dag}: ${ingepland.length} personen = incompleet koppel`);
    }
  }

  const caoOvertredingen = controleerCAOGrenzen(urenTeller, regels.medewerkers, weekCaps);

  log.push(`[AI] Totaal conflicten: ${conflicten.length}`);
  if (caoOvertredingen.length > 0) {
    log.push(`[CAO] ${caoOvertredingen.length} CAO-grens overschrijdingen gevonden`);
  }

  return { rooster, conflicten, log, schoolweekType: type, urenTeller, caoOvertredingen, bezettingsMap, herschikTrace };
}

/**
 * Genereert 4 opeenvolgende weken in één keer.
 *
 * Uren-balans regels:
 *   - Over 4 weken gemiddeld op 4 × contract_uren uitkomen per medewerker
 *   - Per week max 48u (6 diensten) als absolute grens
 *   - Als vorige week ≥ 6 straat-diensten → deze week max 4 straat-diensten (compensatie)
 *
 * Technisch: vaste uren (kantoor, school week B) worden los bijgehouden van straat-uren
 * zodat pre-committed uren nooit als "overplanning" worden geflagged.
 *
 * @param {object}  regels      - de volledige regelset (medewerkers, gemeenten, diensten)
 * @param {object}  afwMaps     - { [weeknummer]: afwezigheidMap } voor elke week
 * @param {number|number[]} startWeek - weeknummer van week 1 (de engine telt door), of de lijst
 *                            weeknummers per positie (het store-pad; zie draaiMaandrooster)
 */
// vastgelegdeWeken (optioneel): { [weeknummer]: weekObject } — al gefilterd op het
// isoJaar van DIT blok (de caller, bv. blok-generator, sleutelt op isoJaar+weeknummer
// en geeft per blok de juiste deelverzameling mee). Die weken worden niet opnieuw
// gegenereerd: hun uren tellen mee in het blokbudget en ze komen ongewijzigd terug.
// voorgeschiedenis (optioneel): het weekobject van de VASTGELEGDE week direct vóór dit blok,
// aangeleverd door de blok-generator. Alleen om over de blokgrens heen aaneengesloten gewerkte
// dagen te kunnen tellen; het telt NIET mee in het blokbudget en komt NIET in het resultaat.
// Uitsluitend lezen — het object komt bij referentie uit de opslag.
// afwUrenMaps (optioneel): { [weeknummer]: { naam: { perType, dagen } } } — handmatig ingevulde
// uren (verlof of ziekte), al uitgerekend door data/verlofuren.js, inclusief de type-controle:
// perType = { [type]: TOTAAL van die week }, dagen = { [dag]: type } voor de door een uren-record
// gedekte dagen. Zelfde route als afwTypeMaps. De engine REKENT HIER NIETS VAN — hij kent de
// records en de vakanties niet: hij telt de weektotalen op en geeft een gedekte dag geen 8u meer.
// null/leeg = niemand heeft ingevulde uren; dan geldt het oude pad (8u per would-be-werkdag).
export function genereerMaandrooster(regels, afwMaps, startWeek, aantalWeken = 4, vastgelegdeWeken = null, afwTypeMaps = null, vakantieMaps = null, voorgeschiedenis = null, afwUrenMaps = null) {
  // Fase 1: volledig ZONDER herschik — byte-identiek aan de huidige engine.
  const fase1 = draaiMaandrooster(regels, afwMaps, startWeek, aantalWeken, null, vastgelegdeWeken, afwTypeMaps, vakantieMaps, voorgeschiedenis, afwUrenMaps);

  // Geen tekorten -> fase 1 ongewijzigd teruggeven.
  const tekortMap = new Map(
    Object.entries(fase1.samenvatting)
      .filter(([, s]) => s.afwijking < 0)
      .map(([k, s]) => [k, s.afwijking])
  );
  if (tekortMap.size === 0) return fase1;
  const tekort = [...tekortMap.keys()].sort(); // alleen voor de [HERPASS]-log hieronder

  // Fase 2 (maximaal ÉÉN herpass, deterministisch): zelfde generatie, maar met
  // de mini-herschik (stap 5c) actief UITSLUITEND voor de medewerkers die
  // fase 1 met een tekort afsloten (whitelist op medewerker-key).
  const fase2 = draaiMaandrooster(regels, afwMaps, startWeek, aantalWeken, tekortMap, vastgelegdeWeken, afwTypeMaps, vakantieMaps, voorgeschiedenis, afwUrenMaps);
  // De [HERPASS]-markering hoort alleen op (her)gegenereerde weken. Vastgelegde weken
  // moeten byte-identiek terugkomen, dus die slaan we over — anders muteren we de
  // meegegeven invoer-objecten en lopen hun log één regel uit de pas.
  const vastNrs = vastgelegdeWeken ? new Set(Object.keys(vastgelegdeWeken).map(Number)) : null;
  fase2.weken.forEach(w => {
    if (vastNrs && vastNrs.has(w.weeknummer)) return;
    w.log.unshift(`[HERPASS] herschik-whitelist: ${tekort.join(', ')}`);
  });
  return fase2;
}

function draaiMaandrooster(regels, afwMaps, startWeek, aantalWeken, herschikWhitelist, vastgelegdeWeken = null, afwTypeMaps = null, vakantieMaps = null, voorgeschiedenis = null, afwUrenMaps = null) {
  // Het weeknummer van elke positie. De maps die binnenkomen (afwMaps, afwTypeMaps, afwUrenMaps,
  // vakantieMaps, vastgelegdeWeken) horen op precies deze nummers gesleuteld te zijn. Een GETAL
  // telt door, startWeek + i: het blokpad, waar een blok per constructie aaneensluit binnen één
  // ISO-jaar. Een LIJST wordt letterlijk gevolgd: zo geeft het store-pad de ECHTE ISO-weken door,
  // ook over het einde van een ISO-jaar (53, 1, 2, 3). Doortellen gaf daar 54, 55, 56, en na een
  // jaar met 53 weken klapten daarmee het A/B-type en de weekendrotatie om (jaargrens-meting.mjs).
  const weekNrs = Array.isArray(startWeek) ? startWeek : Array.from({ length: aantalWeken }, (_, i) => startWeek + i);
  const weken = [];
  // Chronologische historie voor de dagenreeks-teller: dezelfde weken als `weken`, maar met de
  // vastgelegde week van vóór de blokgrens ervóór geplakt. Bewust een APARTE array: `weken` is
  // het resultaat van dit blok en de voorgeschiedenis hoort daar niet in te lekken.
  const historie = voorgeschiedenis ? [voorgeschiedenis] : [];
  const cumStraatUren = {};   // alleen straat-uren (excl. kantoor/school) voor budget-tracking
  const cumTotaalUren = {};   // alle uren inclusief vaste, voor samenvatting-weergave
  const dienstenVorige = {};  // straat-diensten vorige week (compensatie-check)
  const cumAfwUren = {};      // gecrediteerde afwezigheidsuren (totaal) — gevuld NA de weken-loop
  const cumAfwPerType = {};   // dezelfde credit, uitgesplitst per type (label-only)
  const wbTypeLijst = {};     // [k] = type per would-be-werkdag-afwezig, chronologisch
  const straatReductie = {};  // straat-uren die door afwezigheid niet werkbaar zijn (per blok)

  const mwKeys = Object.keys(regels.medewerkers).filter(k => !k.startsWith('_'));

  // Pre-bereken vaste uren + beschikbare straatdagen per week per medewerker
  const vasteUrenMatrix = {};
  const availMatrix = {}; // [k][i] = aantal beschikbare straatdagen in week i (bovengrens)
  mwKeys.forEach(k => {
    cumStraatUren[k] = 0;
    cumTotaalUren[k] = 0;
    dienstenVorige[k] = 0;
    cumAfwUren[k] = 0;
    cumAfwPerType[k] = { verlof: 0, ziekte: 0, anders: 0 };
    wbTypeLijst[k] = [];
    vasteUrenMatrix[k] = Array.from({ length: aantalWeken }, (_, i) =>
      berekenVasteBezeteUren(
        regels.medewerkers[k], schoolweekType(weekNrs[i]), null, vakantieMaps?.[weekNrs[i]] || null
      )
    );
    const mw = regels.medewerkers[k];
    let reductie = 0;
    availMatrix[k] = Array.from({ length: aantalWeken }, (_, i) => {
      const afwDagen = (afwMaps[weekNrs[i]] || {})[mw.naam] || null;
      const vakDagen = vakantieMaps?.[weekNrs[i]] || null;
      const behoefte = Math.max(0, mw.contract_uren - vasteUrenMatrix[k][i]);
      const werkbaarVol = Math.min(behoefte, 8 * beschikbareStraatDagen(mw, weekNrs[i], null, vakDagen));
      const availAfw = beschikbareStraatDagen(mw, weekNrs[i], afwDagen, vakDagen);
      reductie += werkbaarVol - Math.min(behoefte, 8 * availAfw);
      return availAfw;
    });
    straatReductie[k] = reductie;
  });

  // Gepland = ECHT gewerkt (fantoom-K/S op verlofdagen eruit; scheduler ongemoeid)
  // + afwezigheids-credit. De credit zelf wordt NIET hier berekend maar NA de weken-loop,
  // op blok-niveau: per week aftoppen op (contract − gewerkt van díé week) laat een rest
  // achter zodra het contract geen 8-voud is (36u → +4), en een per-week cap van het
  // afgeronde contract zou iemand die weken achtereen weg is juist 40u/week geven.
  // Pas als het hele blok gedraaid is, weet je wat iemand écht gewerkt heeft; daar hoort
  // één cap tegen het blok-contract. Hier verzamelen we alleen de would-be-werkdagen +
  // hun type, chronologisch (weken oplopend, binnen een week DAGEN-volgorde).
  const verwerkUren = (k, weeknummer, i, urenDezeWeek) => {
    const mw = regels.medewerkers[k];
    const wt = schoolweekType(weeknummer);
    const afwDagen = (afwMaps[weeknummer] || {})[mw.naam] || null;
    const vakDagen = vakantieMaps?.[weeknummer] || null;
    const vasteBezet = vasteUrenMatrix[k][i];
    const straatUren = Math.max(0, urenDezeWeek - vasteBezet);
    const gewerkt = urenDezeWeek - (vasteBezet - berekenVasteBezeteUren(mw, wt, afwDagen, vakDagen));

    cumStraatUren[k] += straatUren;
    cumTotaalUren[k] += gewerkt;
    dienstenVorige[k] = Math.floor(straatUren / 8);

    if (afwDagen) {
      const wbDagen = [...afwDagen].filter(d => wouldBeWerkdag(mw, d, wt, weeknummer, regels, vakDagen));
      const typeVanDag = (afwTypeMaps?.[weeknummer] || {})[mw.naam] || {};
      // Handmatig ingevulde uren voor DEZE week als { perType, dagen }, al verdeeld over de
      // HELE periode van hun record door data/verlofuren.js — inclusief de type-controle, die
      // daar staat omdat alleen die laag de rauwe records nog ziet. Hier alleen opzoeken en
      // optellen; zie de kop bij genereerMaandrooster.
      const wkUren = (afwUrenMaps?.[weeknummer] || {})[mw.naam] || null;
      const urenDagen = wkUren?.dagen || {};
      for (const d of DAGEN) {
        if (!wbDagen.includes(d)) continue;
        const type = typeVanDag[d] || 'anders';
        // Is deze dag al door een uren-record van hetzelfde type gedekt, dan is hij betaald
        // en hoort hij NIET nog eens 8u credit via het oude pad te krijgen. Valt hij af —
        // geen ingevuld getal, of een ander type dan de typeMap hem geeft — dan geldt
        // onverkort het oude pad.
        if (urenDagen[d] === type) continue;
        wbTypeLijst[k].push({ type, uren: null });
      }
      // De weektotalen per type gaan ongeclipt naar de vaste stapel: Numbers heeft ze al
      // bepaald. Ze staan bewust LOS van de dagenlus hierboven — een weekdeel hoeft geen
      // would-be-werkdag te hebben om op te landen.
      for (const [t, u] of Object.entries(wkUren?.perType || {})) {
        wbTypeLijst[k].push({ type: t, uren: u });
      }
    }
  };

  for (let i = 0; i < aantalWeken; i++) {
    const weeknummer = weekNrs[i];
    const wekenOver = aantalWeken - i;

    // ── Vastgelegde week: vaste invoer, niet (her)genereren ────────────────────
    // Sla genereerRooster over en zet het opgeslagen weekobject ongewijzigd terug.
    // Tel de uren wél door dezelfde cum-accumulatie als een gegenereerde week,
    // zodat resterendStraat/weekCaps van de OPEN weken automatisch met het
    // RESTBUDGET verdergaan (= het hele mechanisme uit de diagnose).
    const vast = vastgelegdeWeken ? vastgelegdeWeken[weeknummer] : null;
    if (vast) {
      weken.push(vast);
      historie.push(vast); // ook een vastgelegde week is gewerkte historie; alleen lezen
      mwKeys.forEach(k => verwerkUren(k, weeknummer, i, (vast.urenTeller && vast.urenTeller[k]) || 0));
      continue;
    }

    const weekCaps = {};       // ruime cap voor minimumbezetting (Fase 1)
    const weekCapsExtra = {};  // krappe cap voor extra koppels (Fase 2) — gelijkmatig tempo
    const maandTekort = {};    // resterend straat-budget (maand) per mw, input voor de bijvul-klep
    mwKeys.forEach(k => {
      const mw = regels.medewerkers[k];
      const vasteBezet = vasteUrenMatrix[k][i]; // vaste uren DEZE week

      // Totaal straat-budget voor de 4-weken periode (budget minus alle vaste uren)
      const totalVasteBezet = vasteUrenMatrix[k].reduce((s, v) => s + v, 0);
      const totalStraatBudget = Math.max(0, aantalWeken * mw.contract_uren - totalVasteBezet - straatReductie[k]);

      // Resterend straat-budget voor weken i t/m 3
      const resterendStraat = Math.max(0, totalStraatBudget - cumStraatUren[k]);
      maandTekort[k] = resterendStraat;

      // Vast weekplafond: 5 diensten (40u) voor IEDEREEN, ongeacht contract. Geeft schuifruimte
      // binnen het blok (bv. een 32u-er: 3 diensten de ene week, 5 de andere) i.p.v. veelvouden
      // van 8 vast te pinnen op hun blok-gemiddelde. De blok-balans blijft gegarandeerd door
      // totalStraatBudget (contract-gebaseerd, hierboven) + de even-pace throttle; dit plafond
      // is enkel een bovengrens op availDeze/futureCapaciteit, geen blok-target.
      const maxDienstenPerWeek = 5;

      // Beschikbaarheids-bewuste ONDERGRENS (water-filling): hebben de resterende weken samen
      // te weinig straatdagen voor het resterende budget, dan moet deze week minstens het
      // verschil opvangen. Voorkomt dat een krap-beschikbare medewerker (bv. Danny: ma school
      // + wo vrij + weekend-rotatie → even weken maar 3 dagen) tekortkomt doordat de gelijkmatige
      // verdeling zijn drukke (oneven) weken niet genoeg laat bijladen.
      const resterendDiensten = resterendStraat / 8;
      const availDeze = Math.min(maxDienstenPerWeek, availMatrix[k][i]);
      const futureCapaciteit = availMatrix[k].slice(i + 1)
        .reduce((s, a) => s + Math.min(maxDienstenPerWeek, a), 0);
      const ondergrens = Math.max(0, resterendDiensten - futureCapaciteit);

      // Minimum-cap (Fase 1): gelijkmatige verdeling (ceil), maar minstens de ondergrens en
      // nooit meer dan deze week beschikbaar is.
      const dienstenOver = Math.min(availDeze, Math.max(Math.ceil(resterendDiensten / wekenOver), ondergrens));
      let straatCap = dienstenOver * 8;

      // Extra-cap (Fase 2): krap — throttle extra koppels tot een gelijkmatig CUMULATIEF tempo,
      // zodat extra's in vroege weken geen budget opsouperen dat latere weken nodig hebben voor
      // hun minimum. De laatste week absorbeert het restant → iedereen eindigt op ±0u.
      //   - Normale medewerkers: round (half-up) — soepel genoeg om hun uren te halen.
      //   - vast_rotatiepatroon (Haruun/Myrthe): floor (strikt) — hun patroon is ONREGELMATIG
      //     (5 diensten in week B, 4 in week A); round zou ze in een lichte week een extra
      //     Arnhem-dienst laten pakken die budget steelt van hun zware patroonweek.
      // De ondergrens en beschikbaarheid gelden ook hier (anders throttelt het Danny's drukke week).
      const afrond = mw.vast_rotatiepatroon ? Math.floor : Math.round;
      const evenCumStraat = ((i + 1) / aantalWeken) * totalStraatBudget;
      const dienstenExtra = Math.min(availDeze, Math.max(ondergrens, afrond((evenCumStraat - cumStraatUren[k]) / 8)));
      let straatCapExtra = Math.max(0, dienstenExtra) * 8;

      // Compensatieweek: vorige week ≥ 6 straat-diensten → max 4 straat-diensten
      if (dienstenVorige[k] >= 6) {
        straatCap = Math.min(straatCap, 32);
        straatCapExtra = Math.min(straatCapExtra, 32);
      }

      // weekCap = vaste uren + straat cap (vaste uren zijn altijd toegestaan)
      weekCaps[k] = vasteBezet + Math.max(0, straatCap);
      // extra-cap nooit lager dan minimum-cap-equivalent zou onlogisch zijn; extra ≤ min is OK.
      weekCapsExtra[k] = vasteBezet + Math.max(0, straatCapExtra);
    });

    const afwMap = afwMaps[weeknummer] || {};
    // historie.slice(): een MOMENTOPNAME van de weken vóór deze. `historie` groeit hierna door,
    // dus een levende referentie zou een bewaarde kopie stilletjes laten meegroeien.
    // historieCtx: de afwezigheids-/vakantiemaps van de weken VÓÓR deze. De reeks-teller heeft
    // ze nodig om een kantoor- of schooldag die in werkelijkheid een verlofdag was niet als
    // gewerkt te tellen; zonder dat zou de max-aaneengesloten-regel te streng uitvallen.
    const resultaat = genereerRooster(regels, afwMap, weeknummer, weekCaps, weekCapsExtra, maandTekort, herschikWhitelist, vakantieMaps?.[weeknummer] || null, historie.slice(), { afwMaps, vakantieMaps });
    const week = { weeknummer, weekCaps, ...resultaat };
    weken.push(week);
    historie.push(week);

    mwKeys.forEach(k => verwerkUren(k, weeknummer, i, resultaat.urenTeller[k] || 0));
  }

  // ── Afwezigheids-credit op BLOK-niveau ──────────────────────────────────────
  // credit = max(0, min(blok-contract − echt gewerkt, 8 × would-be-werkdagen-afwezig)).
  // Eén cap over het hele blok i.p.v. per week: een 36u-contract laat per week een rest
  // van 4u liggen (8u-diensten vullen 36 nooit exact), maar over het blok verrekent dat
  // tegen de weken waarin iemand 40u draait. Wie het hele blok weg is, krijgt zo nooit
  // meer dan zijn blok-contract gecrediteerd.
  mwKeys.forEach(k => {
    const mw = regels.medewerkers[k];
    // Twee stapels, want ze volgen verschillende regels:
    //   vasteUren  de handmatig ingevulde uren (verlof of ziekte) van dit blok. Die staan VAST —
    //              Numbers heeft ze al bepaald, dus er gaat geen aftopping overheen.
    //   restTypes  alle overige afwezige would-be-werkdagen ('anders', en verlof of ziekte
    //              zonder ingevuld getal). Die houden het oude pad: 8u per dag, afgetopt op
    //              wat er van het blok-gat NA de vaste uren nog over is.
    let vasteUren = 0;
    const vastePerType = {};   // dezelfde vaste uren, opgeteld per type van de dag
    const restTypes = [];
    for (const p of wbTypeLijst[k]) {
      if (p.uren === null) {
        restTypes.push(p.type);
      } else {
        vasteUren += p.uren;
        vastePerType[p.type] = (vastePerType[p.type] || 0) + p.uren;
      }
    }
    const blokGap = Math.max(0, aantalWeken * mw.contract_uren - cumTotaalUren[k] - vasteUren);
    let credit = Math.max(0, Math.min(blokGap, 8 * restTypes.length));
    cumAfwUren[k] = vasteUren + credit;
    // De vaste uren gaan naar de bak van het type van hun eigen dag (p.type — dezelfde
    // typeMap waarop de resttypes hieronder boeken), zodat elk uur in de uitsplitsing het
    // label van zijn dag draagt. Samen zijn die bakken precies vasteUren. Splits daarna
    // dezelfde credit per type over de overige would-be-werkdagen (chronologisch), gecapt
    // op de credit: vasteUren + som van de delen == cumAfwUren, dus SUM(perType) == cumAfwUren.
    for (const [t, uren] of Object.entries(vastePerType)) {
      cumAfwPerType[k][t] = (cumAfwPerType[k][t] || 0) + uren;
    }
    for (const t of restTypes) {
      if (credit <= 0) break;
      const deel = Math.min(8, credit);
      cumAfwPerType[k][t] = (cumAfwPerType[k][t] || 0) + deel;
      credit -= deel;
    }
  });

  const samenvatting = {};
  mwKeys.forEach(k => {
    const mw = regels.medewerkers[k];
    if (isTeamleiderOnly(mw)) return; // teamleider-only niet in de uren-balans/knelpunten
    samenvatting[k] = {
      naam: mw.naam,
      cumUren: cumTotaalUren[k],
      target: aantalWeken * mw.contract_uren,
      afwUren: cumAfwUren[k],
      afwPerType: cumAfwPerType[k],
      afwijking: cumTotaalUren[k] + cumAfwUren[k] - aantalWeken * mw.contract_uren,
    };
  });

  return { weken, samenvatting };
}
